/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  createAsyncThunk,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";
import type {
  Draft,
  PayloadAction,
  ThunkDispatch,
  UnknownAction
} from "@reduxjs/toolkit";
import { ActionGroup, ActionType, DateTime } from "@tivocorp/data-domain-lib";
import type {
  TAction,
  TActionsParams,
  TCustomActionList,
  GuideMenuItemV2
} from "@tivocorp/data-domain-lib";

import type {
  TAnalyticsFields,
  TChannel,
  TGridGuideRow,
  TGuideCell,
  TPreview
} from "../../model/types";
import { DiagnosticSeverity } from "../../model/types";
import {
  ActionsInteractor,
  ChannelsInteractor,
  PreviewInteractor,
  PostSigninInteractor
} from "../../model/interactor";
import { FilteredGuideInteractor } from "../../model/interactor/filteredGuide";
import { DeviceModel, DeviceFeatures } from "../../model/device";
import { ChannelsServiceError, ErrorTypes } from "../../model/error";
import {
  GUIDE_ERROR_CODE,
  GUIDE_SCREEN_NAME,
  GuideErrorMapper,
  isCancellation
} from "./guideErrorMapper";
import { Locale, LocaleKeys } from "../../model/locale";
import {
  Log,
  DiagnosticLogger,
  OperationalLogger,
  AnalyticsLogger
} from "../../model/logging";
import { StorageAdapter } from "../../model/storage";
import { STORAGE_KEY_GUIDE_SELECTED_FILTER_IDS } from "../../model/storage/storageConfig";
import { VideoPlaybackController } from "../../model/videoplayer/VideoPlaybackController";
import { PlaybackContentType } from "../../model/videoplayer/enums";
import type { IPlayable } from "../../model/videoplayer/interfaces";
import { GuideCache } from "./guideCache";
import { getGuideFavoritesFallbackFilter } from "./guideFilters";
import {
  GUIDE_HIGHLIGHT_ROW_MAX,
  GUIDE_SIDEBAR_DIRECTIONAL_LOCK_MS,
  GUIDE_START_CHANNEL_ROW,
  GUIDE_PREFETCH_CH_THRESHOLD,
  GUIDE_BATCH_SIZE,
  GUIDE_INITIAL_FUTURE_PREFETCH_MINUTES,
  GUIDE_VISIBLE_ROW_COUNT,
  GUIDE_FILTER_SIDEBAR_MAX_VISIBLE_ITEMS,
  GUIDE_WINDOW_DURATION_MINUTES,
  GUIDE_TIME_SLOT_INTERVAL_MINUTES,
  GUIDE_JUMP_MAX_DIGITS,
  GUIDE_JUMP_ENTRY_TIMEOUT_MS,
  GUIDE_JUMP_ERROR_DISPLAY_MS,
  GUIDE_NAV_SETTLE_DEBOUNCE_MS,
  GUIDE_AUTO_REFRESH_INTERVAL_MS,
  GUIDE_FILTER_APPLY_DEBOUNCE_MS
} from "../../config";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";
import { selectPreviousUrl } from "../screens";
import { LocationUrls } from "../../model/navigation/LocationUrls";
import { GuideTimeWindow, GuideNavMode } from "./guide.types";
import type {
  GuideCellView,
  GuideJumpToChannelState,
  GuideVisibleChannelRow,
  GuideVisibleRowView,
  GuideRowsState,
  GuideState,
  LoadGuideRowsPayload,
  TGuideFilterItem
} from "./guide.types";
import {
  applyNavigateUp,
  applyNavigateDown,
  applyHorizontalLeftStep,
  applyHorizontalRightStep,
  applyShiftForward,
  applyShiftBackward,
  applyAutoRefreshAdvance,
  applyGuideSessionAnchorRefresh,
  resolvePrefetchRows,
  isImmediatePastWindow,
  shouldShiftForwardAfterRightStep,
  shouldTriggerNonPresentWindowGuardOnLeft,
  isBackwardShiftBlockedAtBoundary,
  getGuideStartTime,
  buildVisibleChannelRows,
  buildVisibleGuideRows,
  buildTimeSlotLabels,
  computeChannelScrollOffsetForChannel,
  getEffectiveVerticalPosition,
  getMaxHorizontalPosition,
  findHorizontalPositionForTime,
  getGuideWindowChannels,
  getChannelPrefetchPlan,
  shouldPrefetchTime,
  calculateInitialTimeWindow,
  resolveJumpChannelIndex,
  computeJumpScrollTarget,
  getEffectiveChannelCount,
  applyFavoriteChannelUpdateToState,
  findLowestNumberedFavoriteChannel,
  getFavoriteFilterId,
  isFavoritesFilterSelected,
  isFilterItemDisabled,
  nearestEnabledFilterIndex,
  hasSelectableFilterItem,
  stepEnabledFilterIndex,
  matchesActiveGuideFilters,
  isAnyGuideFilterActive,
  findUnmatchedCategoryChannelIds
} from "./guideNavigation";
import type {
  GuideWindowChannels,
  GuideChannelPrefetchPlan,
  InitialTimeWindow,
  JumpScrollTarget
} from "./guideNavigation";

const TAG: string = "guideSlice";

/** Operational log message for a failed `/v2/guideChannelIds` fetch. */
const FILTER_CHANNEL_IDS_FAILURE_MESSAGE: string =
  "Failed to fetch guide filter channel ids";

/** screen_id for Guide analytics/operational events. */
export const GUIDE_SCREEN_ID = "guide_grid";

export { GUIDE_SCREEN_NAME };

/** list_name for Guide's program grid, used by contentHighlighted/contentSelected/viewedContentDetails. */
export const GUIDE_LIST_NAME = "GridGuide";

/**
 * Debounce delay before GuidePreview dispatches {@link fetchGuideOfferPreview}
 * for the focused program cell. Owned here (rather than in GuidePreview)
 * because `fetchGuideOfferPreview` also uses it as `contentHighlighted`'s
 * `dwell_time_ms` — a single source keeps the logged dwell time truthful to
 * the actual debounce.
 */
export const GUIDE_PREVIEW_DEBOUNCE_DELAY_MS: number = 500;

/** Shared analytics logger for all Guide screen events. */
export const GuideAnalyticsLogger = AnalyticsLogger.create({
  screenId: GUIDE_SCREEN_ID,
  screenName: GUIDE_SCREEN_NAME
});

/**
 * Screen the user was on right before Guide, captured once at mount
 * (`captureGuideEntryScreen`) and read back by `logGuideViewedScreen` at
 * unmount. Kept outside Redux state — it is pure analytics bookkeeping for a
 * single-instance screen, not app state anything else needs to read.
 */
let guideEntryScreenId: string = "";

/**
 * Builds the pgd/pgd_details fields documented for Guide's
 * contentHighlighted/contentSelected/viewedContentDetails events from the
 * currently focused program cell and channel.
 *
 * Fields the data model has nothing to populate (`year`, `genre`,
 * `season_number`, `episode_number`, `collection_title` — not present on
 * `TGuideCell`) are still sent as empty strings rather than omitted, so the
 * event's field set matches the spec's documented shape exactly.
 *
 * `channel_id` carries the *station* identifier here, not `TChannel`'s own
 * `channelId` — see "guide logging.md" → PGD Fields → Naming note: Guide's
 * event tables use `channel_id` as the literal counterpart of the master
 * spec's `station_id`, and don't use a separate `station_id` field.
 */
/** First defined value among the candidates, or `undefined` if none are. */
const firstDefined = <T>(...values: Array<T | undefined>): T | undefined => {
  for (const value of values) {
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
};

/**
 * Reads a field off `matchingPreview`, or `undefined` when there is no
 * matched preview. Centralizes the null check so callers deriving several
 * fields from the same preview don't each carry their own branch — that
 * branching is what drove buildGuideContentFields's cognitive complexity
 * over budget.
 */
const fromPreview = <T>(
  matchingPreview: TPreview | null,
  field: (preview: TPreview) => T | undefined
): T | undefined =>
  matchingPreview === null ? undefined : field(matchingPreview);

const stringOrEmpty = (value: string | undefined): string =>
  value === undefined ? "" : value;

const numberOrEmpty = (value: number | undefined): string =>
  value === undefined ? "" : String(value);

/**
 * Error fields for diagnostic logging. Kept out of the catch blocks so the
 * thunks that share them stay under the cognitive complexity budget.
 */
const channelsErrorCode = (error: unknown, fallback: string): string =>
  error instanceof ChannelsServiceError
    ? error.getUIErrorwithSubErrorCode()
    : fallback;

const channelsErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ChannelsServiceError && error.getMessage().length > 0) {
    return error.getMessage();
  }
  return error instanceof Error ? error.message : fallback;
};

const errorTypeName = (error: unknown): string =>
  error !== null && typeof error === "object"
    ? error.constructor.name
    : typeof error;

/**
 * Content-side pgd/pgd_details fields derivable from the focused cell, with
 * gaps filled from a matching preview when the data model has nothing else.
 */
const buildGuideContentFields = (
  cell: TGuideCell,
  matchingPreview: TPreview | null
): Partial<TAnalyticsFields> => {
  const cellTitle: string | undefined =
    cell.title === undefined || cell.title.length === 0
      ? undefined
      : cell.title;
  const contentTitle: string | undefined = firstDefined(
    cellTitle,
    fromPreview(matchingPreview, (preview) => preview.episodeTitle)
  );
  const originalAirDate: string | undefined = fromPreview(
    matchingPreview,
    (preview) => preview.firstAiredDate
  );
  const previewYear: number | undefined = firstDefined(
    fromPreview(matchingPreview, (preview) => preview.movieYear),
    fromPreview(matchingPreview, (preview) => preview.premierYear)
  );
  const seasonNumber: number | undefined = fromPreview(
    matchingPreview,
    (preview) => preview.seasonNumber
  );
  const episodeNumber: number | undefined = fromPreview(
    matchingPreview,
    (preview) => preview.episodeNumber
  );

  // For episodic content, TPreview's base `title` is the series/collection
  // title — `episodeTitle` is the specific episode's title, used above for
  // content_title. Only trustworthy together with a matched preview: a
  // movie/non-episodic preview's `title` is the content's own title, not a
  // collection title, but `matchingPreview` already guarantees this preview
  // is for the currently focused offer either way.
  const collectionTitle: string | undefined = fromPreview(
    matchingPreview,
    (preview) =>
      preview.episodeTitle === undefined ? undefined : preview.title
  );

  return {
    content_id: cell.contentId,
    content_title: stringOrEmpty(contentTitle),
    content_type: cell.contentType,
    collection_id: cell.collectionId,
    collection_title: stringOrEmpty(collectionTitle),
    program_start_time: String(cell.startTime.getTime()),
    original_air_date: stringOrEmpty(originalAirDate),
    year: numberOrEmpty(previewYear),
    genre: "",
    season_number: numberOrEmpty(seasonNumber),
    episode_number: numberOrEmpty(episodeNumber),
    tile_id: cell.offerId
  };
};

/**
 * Channel-side pgd fields derivable from the focused channel, with
 * `pgd_resolution` preferring the offer-specific preview value (more
 * precise than the channel's general broadcast resolution) when available.
 * `content_network` is channel-level data (the network the channel is
 * affiliated with), not content-level, despite living in the pgd_details
 * category.
 */
const buildGuideChannelFields = (
  channel: TChannel | null,
  matchingPreview: TPreview | null
): Partial<TAnalyticsFields> => {
  const providerId: string | undefined =
    channel !== null ? channel.linearProviderPartnerId : undefined;
  const resolution: string | undefined = firstDefined(
    matchingPreview !== null ? matchingPreview.videoResolution : undefined,
    channel !== null ? channel.videoResolution : undefined
  );

  return {
    channel_id: channel !== null ? channel.stationId : "",
    channel_num: channel !== null ? channel.channelNumber : "",
    channel_call_sign: channel !== null ? channel.callSign : "",
    content_network:
      channel !== null && channel.affiliate !== undefined
        ? channel.affiliate
        : "",
    provider_id: providerId !== undefined ? providerId : "",
    pgd_resolution: resolution !== undefined ? resolution : ""
  };
};

const buildGuidePgdFields = (
  cell: TGuideCell,
  channel: TChannel | null,
  // `| undefined` defensively, on top of the reducer's `TPreview | null`
  // typing: some existing test fixtures build a partial GuideState that
  // omits previewData entirely, so it can genuinely be undefined at runtime.
  preview: TPreview | null | undefined,
  previewOfferId: string | null | undefined
): Partial<TAnalyticsFields> => {
  // Only trust preview data when it was actually fetched for this cell's
  // offer — TPreview carries no offerId of its own to check directly, so
  // previewDataOfferId (captured from the fetch thunk's own args) stands in.
  // During rapid navigation, previewData can briefly still be the previous
  // selection's fulfilled result after focus has already moved on.
  const matchingPreview: TPreview | null =
    preview !== null &&
    preview !== undefined &&
    previewOfferId !== null &&
    previewOfferId !== undefined &&
    previewOfferId === cell.offerId
      ? preview
      : null;

  return {
    ...buildGuideContentFields(cell, matchingPreview),
    ...buildGuideChannelFields(channel, matchingPreview)
  };
};

/**
 * Debounce window for per-channel favorite persistence (§2.3). Configurable
 * — not hardcoded inline at the call site so it can be tuned without
 * touching the thunk logic.
 */
const FAVORITE_PERSIST_DEBOUNCE_MS: number = 400;

/** A pending debounced favorite-persist job for a single channel. */
interface FavoritePersistJob {
  timer: ReturnType<typeof setTimeout>;
  version: number;
}

/**
 * Per-channel debounce/version tracking for favorite persistence (§2.3).
 * Module-level (not Redux state) since timers are not serializable and this
 * is pure scheduling infrastructure, not application state.
 */
const favoritePersistJobs: Map<string, FavoritePersistJob> = new Map();

/**
 * Cancels all pending debounced favorite-persist jobs.
 *
 * Must be called when the guide screen unmounts so that no orphaned
 * channel-settings API calls fire after the screen (and its focused-channel
 * context) is gone (§2.3 lifecycle, edge case #7).
 */
export const cleanupFavoritePersistJobs = (): void => {
  favoritePersistJobs.forEach((job: FavoritePersistJob): void => {
    clearTimeout(job.timer);
  });
  favoritePersistJobs.clear();
};

// ─── Jump-to-Channel Module-Level Timers ──────────────────────────────────────
// GUIDE_JUMP_MAX_DIGITS, GUIDE_JUMP_ENTRY_TIMEOUT_MS, and
// GUIDE_JUMP_ERROR_DISPLAY_MS are defined in ../../config.

/**
 * Module-level timer for the per-digit inactivity timeout.
 * Non-serializable — must NOT live in Redux state.
 */
let jumpEntryTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Module-level timer for the error-state auto-dismiss.
 * Non-serializable — must NOT live in Redux state.
 */
let jumpErrorTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Module-level debounce timer for settling the guide after a burst of
 * directional navigation. Deliberately module-level rather than component or
 * ScreenController state: GuideScreenController is rebuilt on nearly every
 * keystroke (its useMemo depends on callbacks that close over the
 * ever-changing focused cell), so any per-instance timer is silently dropped
 * mid-burst. Module state survives that churn.
 * Non-serializable — must NOT live in Redux state.
 */
let guideSettleTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Module-level debounce timer for the guide filter apply flow (§5.2):
 * persisting the selection, resolving category ids, clearing the EPG cache,
 * repositioning the anchor, and reloading the window. Deliberately
 * module-level (not Redux state) since timers aren't serializable — mirrors
 * `favoritePersistJobs`/`guideSettleTimeoutId`.
 */
let guideFilterApplyTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Bumped on every `toggleGuideFilter` dispatch; a scheduled apply only runs
 * its network/cache/reload steps when it's still the most recent one by the
 * time its debounce elapses. Guards against a settle that was already
 * superseded by a later toggle landing before the timer callback runs
 * (belt-and-suspenders alongside the `clearTimeout` below, which normally
 * prevents the stale callback from firing at all).
 */
let guideFilterApplyVersion: number = 0;

/**
 * Cancels the pending debounced guide-filter apply job, if any.
 *
 * Must be called when the guide screen unmounts so a stale apply (cache
 * clear + window reload) never fires after the screen is gone (mirrors
 * `cleanupFavoritePersistJobs`).
 */
export const cleanupGuideFilterApplyJob = (): void => {
  if (guideFilterApplyTimeoutId !== null) {
    clearTimeout(guideFilterApplyTimeoutId);
    guideFilterApplyTimeoutId = null;
  }
};

const INITIAL_JUMP_TO_CHANNEL_STATE: GuideJumpToChannelState = {
  digits: "",
  isVisible: false,
  isError: false
};

const INITIAL_GUIDE_ROWS_STATE: GuideRowsState = {
  currentRows: [],
  futureRows: [],
  pastRows: []
};

const INITIAL_STATE: GuideState = {
  channels: [],
  channelCount: 0,
  error: "",
  isLoading: false,
  currentWindowStartTime: null,
  isPastGuide: false,
  guideRows: INITIAL_GUIDE_ROWS_STATE,
  verticalPosition: GUIDE_START_CHANNEL_ROW,
  horizontalPosition: 1,
  channelScrollOffset: 0,
  futureWindowStartTime: null,
  pastWindowStartTime: null,
  guideSessionStartMs: 0,
  highlightAnchorTimeMs: 0,
  isSidebarFocused: false,
  sidebarFocusIndex: 0,
  sidebarEnteredAtMs: 0,
  windowLoadGeneration: 0,
  navMode: GuideNavMode.IDLE,
  guideLoadingTimedOut: false,
  selectedFilterIds: [],
  hasFavoriteChannels: true,
  isNpvrRecordingAllowed: false,
  previewData: null,
  previewDataOfferId: null,
  isPreviewLoading: false,
  isGuideRowsFailed: false,
  jumpToChannel: INITIAL_JUMP_TO_CHANNEL_STATE,
  filterItems: [getGuideFavoritesFallbackFilter()],
  categoryFilterChannelIds: [],
  categoryFilterResolutionStatus: "idle",
  isFilterSidebarExpanded: false,
  preSidebarEntryPosition: null
};

// ─── Async Thunks ───

type GuideThunkState = { guide: GuideState };

/**
 * Fetches all channels from the ChannelsService for the guide grid.
 * Stores TChannel[] from the domain lib directly in Redux state.
 */
export const loadGuideChannels = createAsyncThunk<
  { channels: TChannel[]; channelCount: number },
  void,
  { rejectValue: string }
>("guide/loadGuideChannels", async (_: void, { rejectWithValue }) => {
  try {
    Log.i(TAG, "loadGuideChannels() - Fetching channels from ChannelsService");
    const interactor: ChannelsInteractor = new ChannelsInteractor();
    const result = await interactor.fetchAllChannels();
    Log.d(
      TAG,
      "loadGuideChannels() SUCCESS - loaded " +
        result.channels.length +
        " channels"
    );
    DiagnosticLogger.log(
      "guide_lifecycle",
      "loadGuideChannels succeeded",
      DiagnosticSeverity.Info,
      { step: "channels_loaded", channel_count: String(result.channels.length) }
    );

    // Seed the in-memory channel window with the first slice of channels.
    // Channel metadata stays persisted in IndexedDB (CHANNELS); the cache
    // keeps only GUIDE_TOTAL_CACHED_CHANNELS in memory for the guide grid.
    GuideCache.getInstance().setChannelWindow(
      result.channels,
      0,
      result.channels.length
    );

    return { channels: result.channels, channelCount: result.channelCount };
  } catch (error: unknown) {
    Log.e(TAG, "loadGuideChannels()", error);
    const errorCode: string = channelsErrorCode(
      error,
      GUIDE_ERROR_CODE.CHANNELS_LOAD_FAILED
    );
    const message: string = channelsErrorMessage(
      error,
      "Failed to load guide channels"
    );
    const errorType: string = errorTypeName(error);
    DiagnosticLogger.log(
      "guide_lifecycle",
      "loadGuideChannels failed",
      DiagnosticSeverity.Error,
      {
        step: "channels_load_failed",
        error_code: errorCode,
        error_type: errorType
      }
    );
    OperationalLogger.logUiError({
      errorCode: GuideErrorMapper.toCompositeCode(
        GUIDE_ERROR_CODE.CHANNELS_LOAD_FAILED,
        error
      ),
      errorMessage: GuideErrorMapper.toErrorMessage(
        error,
        "Failed to load guide channels"
      ),
      screen: GUIDE_SCREEN_NAME
    });
    return rejectWithValue(message);
  }
});

/**
 * Fetches NPVR allow-recording flag from the DeviceFeatures.
 */
export const fetchGuideNpvrAllowRecording = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>("guide/fetchNpvrAllowRecording", async (_: void, { rejectWithValue }) => {
  try {
    Log.d(TAG, "fetchGuideNpvrAllowRecording() called");
    const interactor: PostSigninInteractor = new PostSigninInteractor();
    const enabled: boolean =
      await DeviceFeatures.getNpvrAllowedRecording(interactor);
    DiagnosticLogger.log(
      "guide_lifecycle",
      "fetchGuideNpvrAllowRecording succeeded",
      DiagnosticSeverity.Info,
      { enabled: String(enabled) }
    );
    return enabled;
  } catch (error: unknown) {
    Log.e(TAG, "fetchGuideNpvrAllowRecording()", error);
    const errorMessage: string = OperationalLogger.describeErrorMessage(error);
    const errorCode: string =
      ErrorTypes.NPVR_ALLOW_RECORDING_REQUEST_FAILED_R504;

    OperationalLogger.logError({ errorCode, errorMessage });
    return rejectWithValue(errorMessage);
  }
});

/**
 * Fetches the dynamic guide filter list from the `/v2/guideMenuItems` API,
 * maps the raw domain items to {@link TGuideFilterItem} with pre-resolved icon
 * URLs, and handles the fallback to a single Favorites item when the API fails
 * or returns an empty list.
 *
 * On success: stores the ordered `TGuideFilterItem[]` in `state.filterItems`.
 * On failure or empty response: falls back to a single Favorites item.
 * Preserves server ordering.
 */
export const loadGuideFilters = createAsyncThunk<
  TGuideFilterItem[],
  void,
  { rejectValue: string }
>("guide/loadGuideFilters", async (_: void) => {
  let menuItems: Array<GuideMenuItemV2>;

  try {
    Log.d(TAG, "loadGuideFilters() called");
    const interactor: FilteredGuideInteractor = new FilteredGuideInteractor();
    menuItems = await interactor.fetchGuideMenuItems({});
  } catch (error: unknown) {
    Log.w(
      TAG,
      "loadGuideFilters() - guideMenuItemsGetV2 failed; using favorites fallback",
      error
    );
    OperationalLogger.logUiError({
      errorCode: GuideErrorMapper.toCompositeCode(
        GUIDE_ERROR_CODE.FILTERS_FETCH_FAILED,
        error
      ),
      errorMessage: GuideErrorMapper.toErrorMessage(
        error,
        "Failed to load guide filters"
      ),
      screen: GUIDE_SCREEN_NAME
    });
    return [getGuideFavoritesFallbackFilter()];
  }

  if (menuItems.length === 0) {
    Log.w(
      TAG,
      "loadGuideFilters() - guideMenuItemsGetV2 returned empty list; using favorites fallback"
    );
    return [getGuideFavoritesFallbackFilter()];
  }

  return menuItems.map((item: GuideMenuItemV2): TGuideFilterItem => {
    const isFavorite: boolean = item.isFavorite === true;
    if (isFavorite)
      return {
        id: item.menuItemId,
        label: item.title,
        iconUrl: null,
        focusedIconUrl: null,
        isFavorite: true
      };

    return {
      id: item.menuItemId,
      label: item.title,
      iconUrl: FilteredGuideInteractor.buildFilterIconUrl(
        item.menuItemId,
        false
      ),
      focusedIconUrl: FilteredGuideInteractor.buildFilterIconUrl(
        item.menuItemId,
        true
      ),
      isFavorite: false
    };
  });
});

/** Selects the Favorites filter's id — see `getFavoriteFilterId`. */
export const selectFavoriteFilterId = (state: UnityRootState): string =>
  getFavoriteFilterId(state.guide);

/**
 * Returns the non-favorite (category) subset of `selectedFilterIds` — the
 * ids that actually need resolving against `/v2/guideChannelIds`.
 *
 * @param state Current guide state snapshot (read-only or Draft).
 * @returns Selected filter ids excluding the Favorites id, if any.
 */
export const getSelectedCategoryFilterIds = (
  state: GuideState | Draft<GuideState>
): string[] => {
  const favoriteFilterId: string = getFavoriteFilterId(state);
  return state.selectedFilterIds.filter(
    (id: string): boolean => id !== favoriteFilterId
  );
};

/** True when any channel in the (unfiltered) list is a favorite. */
const hasAnyFavoriteChannel = (channels: TChannel[]): boolean =>
  channels.some((channel: TChannel): boolean => channel.isFavorite === true);

/** Persists the guide filter selection; failures are logged, not thrown. */
const persistSelectedFilterIds = async (ids: string[]): Promise<void> => {
  try {
    await StorageAdapter.getInstance().set(
      STORAGE_KEY_GUIDE_SELECTED_FILTER_IDS,
      ids,
      Number.POSITIVE_INFINITY
    );
  } catch (persistError: unknown) {
    Log.w(TAG, "persistSelectedFilterIds() - persisting failed", persistError);
  }
};

/**
 * Restores the persisted guide filter selection (Favorites and/or one or
 * more categories) from local storage at guide initialization.
 *
 * Read failures, or no persisted value, default to an empty selection (no
 * filters active) — the persisted state is read once at init, never
 * continuously observed afterward. Restored as persisted; a stale Favorites
 * id (nothing is favorited any more) is dropped afterwards by
 * `restoreAndResolveGuideFilterSelection`.
 */
export const restoreGuideFilterSelection = createAsyncThunk<
  string[],
  void,
  { state: UnityRootState }
>("guide/restoreGuideFilterSelection", async (_arg) => {
  try {
    const storage: StorageAdapter = StorageAdapter.getInstance();
    const persisted: string[] | null = await storage.get<string[]>(
      STORAGE_KEY_GUIDE_SELECTED_FILTER_IDS
    );

    if (
      persisted === null ||
      persisted === undefined ||
      persisted.length === 0
    ) {
      return [];
    }

    return persisted;
  } catch (error: unknown) {
    Log.w(
      TAG,
      "restoreGuideFilterSelection() - read failed, defaulting to []",
      error
    );
    return [];
  }
});

/**
 * Restores the persisted guide filter selection and, when it includes any
 * category ids, resolves them against `/v2/guideChannelIds` before
 * returning — so the very first paint of the grid after mount is never
 * unfiltered-then-narrowed. Requires `filterItems` to already be loaded (the
 * caller must dispatch/await `loadGuideFilters()` first) so the Favorites id
 * can be identified correctly by `restoreGuideFilterSelection`'s guard.
 *
 * Skips the network call entirely when the restored selection has no
 * non-favorite ids, mirroring `toggleGuideFilter`.
 */
export const restoreAndResolveGuideFilterSelection = createAsyncThunk<
  void,
  void,
  { state: UnityRootState }
>(
  "guide/restoreAndResolveGuideFilterSelection",
  async (_arg, { dispatch, getState }) => {
    await dispatch(restoreGuideFilterSelection());

    // The persisted selection may include Favorites although every favorite
    // was removed from another flow since — drop it silently so the guide
    // doesn't open on an empty filtered view.
    try {
      const allChannels: TChannel[] = (
        await new ChannelsInteractor().fetchAllChannels()
      ).channels;
      const hasFavorites: boolean = hasAnyFavoriteChannel(allChannels);
      dispatch(setHasFavoriteChannels(hasFavorites));
      const restored: GuideState = (getState() as GuideThunkState).guide;
      if (!hasFavorites && isFavoritesFilterSelected(restored)) {
        dispatch(removeGuideFilterSelection(getFavoriteFilterId(restored)));
        await persistSelectedFilterIds(
          (getState() as GuideThunkState).guide.selectedFilterIds
        );
      }
    } catch (error: unknown) {
      Log.w(
        TAG,
        "restoreAndResolveGuideFilterSelection() - favorites check failed",
        error
      );
      const errorCode: string = channelsErrorCode(error, "");
      const errorType: string = errorTypeName(error);
      DiagnosticLogger.log(
        "guide_lifecycle",
        "restoreAndResolveGuideFilterSelection favorites check failed",
        DiagnosticSeverity.Warning,
        {
          step: "favorites_check_failed",
          error_code: errorCode,
          error_type: errorType
        }
      );
    }

    const state: GuideState = (getState() as GuideThunkState).guide;
    const categoryIds: string[] = getSelectedCategoryFilterIds(state);

    if (categoryIds.length === 0) {
      dispatch(
        applyCategoryFilterChannelIds({ status: "idle", channelIds: [] })
      );
    } else {
      try {
        const interactor: FilteredGuideInteractor =
          new FilteredGuideInteractor();
        const channelIds: string[] = await interactor.fetchGuideChannelIds({
          menuItemId: new Set(categoryIds)
        });
        dispatch(
          applyCategoryFilterChannelIds({ status: "resolved", channelIds })
        );
      } catch (error: unknown) {
        Log.e(
          TAG,
          "restoreAndResolveGuideFilterSelection() - fetchGuideChannelIds failed",
          error
        );
        if (!isCancellation(error)) {
          OperationalLogger.logUiError({
            errorCode: GuideErrorMapper.toCompositeCode(
              GUIDE_ERROR_CODE.FILTER_CHANNEL_IDS_FETCH_FAILED,
              error
            ),
            errorMessage: GuideErrorMapper.toErrorMessage(
              error,
              FILTER_CHANNEL_IDS_FAILURE_MESSAGE
            ),
            screen: GUIDE_SCREEN_NAME
          });
        }
        dispatch(applyCategoryFilterChannelIdsFailed());
      }
    }

    // Narrows state.channels to the restored selection before the initial
    // window loads — mirrors toggleGuideFilter's apply flow so the guide
    // grid's very first frame is never unfiltered-then-narrowed, and so
    // reads state.channels directly rather than re-deriving a filtered view.
    const resolvedState: GuideState = (getState() as GuideThunkState).guide;
    await refreshGuideChannelsForActiveFilters(dispatch, resolvedState);
  }
);

/**
 * Fetches offer preview data for the currently focused guide program cell.
 *
 * Called by GuidePreview after a 200 ms debounce. The thunk signal wires
 * directly into the interactor so that aborting the dispatched promise
 * (returned by dispatch) cancels any in-flight network request.
 * Pending: clears previous preview and sets loading flag.
 * Fulfilled: stores the fetched TPreview in state.
 * Rejected (abort): loading flag cleared, previous data retained for smooth transition.
 * Rejected (error): preview data cleared and error logged (also logged inside catch).
 */
export const fetchGuideOfferPreview = createAsyncThunk<
  TPreview,
  { offerId: string; channelNumber: string | undefined },
  { state: UnityRootState; rejectValue: string }
>(
  "guide/fetchGuideOfferPreview",
  async (params, { signal, getState, rejectWithValue }) => {
    try {
      Log.i(
        TAG,
        "fetchGuideOfferPreview() - fetching for offerId " + params.offerId
      );

      // contentHighlighted — this thunk is dispatched exactly when the
      // preview debounce settles, so firing here (rather than from the view)
      // needs no selectors in GuidePreview beyond what it already reads to
      // dispatch this thunk in the first place.
      const state: UnityRootState = getState();
      const highlightedCell: TGuideCell | null = selectFocusedGuideCell(state);
      if (highlightedCell !== null) {
        GuideAnalyticsLogger.logContentHighlighted({
          list_name: GUIDE_LIST_NAME,
          list_position: String(
            selectEffectiveGuideHighlightVerticalPosition(state)
          ),
          tile_position: String(
            selectEffectiveGuideHighlightHorizontalPosition(state)
          ),
          dwell_time_ms: String(GUIDE_PREVIEW_DEBOUNCE_DELAY_MS),
          reason: "dwell",
          content_duration: String(highlightedCell.duration),
          ...buildGuidePgdFields(
            highlightedCell,
            selectFocusedGuideChannel(state),
            // Always null here: .pending already cleared previewData before
            // this payload creator ran, since it fires on the same debounce
            // settle that just started this preview fetch.
            selectGuidePreviewData(state),
            selectGuidePreviewDataOfferId(state)
          )
        });
      }

      const interactor: PreviewInteractor = new PreviewInteractor();
      return await interactor.fetchOfferPreviewV5(
        { offerId: params.offerId, channelNumber: params.channelNumber },
        false,
        { signal }
      );
    } catch (error: unknown) {
      Log.e(TAG, "fetchGuideOfferPreview()", error);
      if (!signal.aborted) {
        OperationalLogger.logUiError({
          errorCode: GuideErrorMapper.toCompositeCode(
            GUIDE_ERROR_CODE.PREVIEW_FETCH_FAILED,
            error
          ),
          errorMessage: GuideErrorMapper.toErrorMessage(
            error,
            "Failed to fetch guide offer preview"
          ),
          screen: GUIDE_SCREEN_NAME
        });
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch guide offer preview");
    }
  }
);

/**
 * Orchestrates the initial guide cache population (spec §6).
 *
 * Steps:
 * 1. Fetch channel metadata and seed the channel cache window.
 * 2. Issue a single fetchRange call covering [fetchStart, fetchEnd] for all
 *    cached channels so the cache is pre-warmed on both sides of the visible
 *    window.
 * 3. Perform a cache-hit fetch for the visible portion and merge rows into
 *    Redux state.
 * 4. Pre-warm the 4-hour future window beyond the initial fetch range so that
 *    subsequent right-navigation is served from memory. Best-effort: failures
 *    are swallowed so they never block the visible render.
 */
export const loadGuide = createAsyncThunk<void, void>(
  "guide/loadGuide",
  async (_arg, { dispatch, getState }) => {
    // Step 1: Fetch channels (also seeds the channel cache window).
    const channelsResult = await dispatch(loadGuideChannels());
    if (!loadGuideChannels.fulfilled.match(channelsResult)) {
      Log.e(TAG, "loadGuide — loadGuideChannels failed");
      return;
    }

    const state: GuideState = (getState() as GuideThunkState).guide;

    if (state.channels.length === 0) {
      return;
    }

    const channelWindow: GuideWindowChannels = getGuideWindowChannels(state);
    if (channelWindow.channels.length === 0) {
      return;
    }

    const timeWindow: InitialTimeWindow = calculateInitialTimeWindow();
    const fetchStartMs: number = timeWindow.fetchStart.getTime();
    const fetchEndMs: number = timeWindow.fetchEnd.getTime();

    const cache: GuideCache = GuideCache.getInstance();
    cache.setChannelWindow(
      channelWindow.channels,
      channelWindow.startGlobalIndex,
      state.channels.length
    );

    // Step 2: Single 2.5-hour fetch ([fetchStart, fetchEnd]) for all channels.
    // [REQ-GUIDEBATCH-FUN-013]: 30 min past + 90 min visible + 30 min future.
    try {
      await cache.fetchRange(
        timeWindow.visibleStart,
        channelWindow.channels,
        fetchStartMs,
        fetchEndMs
      );
    } catch (error: unknown) {
      Log.e(TAG, "loadGuide — initial fetch failed", error);
      dispatch(setGuideRowsFailed(true));
      return;
    }

    // Step 3: Cache-hit fetch for the visible portion, then render.
    try {
      const rows: TGridGuideRow[] = await cache.fetch(
        timeWindow.visibleStart,
        channelWindow.channels
      );
      dispatch(
        mergeGuideRows({
          rows,
          timeWindow: GuideTimeWindow.CURRENT,
          startGlobalIndex: channelWindow.startGlobalIndex
        })
      );
      dispatch(setGuideRowsFailed(false));
    } catch (error: unknown) {
      Log.e(TAG, "loadGuide — render fetch failed", error);
      dispatch(setGuideRowsFailed(true));
    }

    // Step 4: Pre-warm the 4-hour future window beyond the initial fetch.
    // Runs sequentially AFTER the visible window has been dispatched to Redux
    // so it never delays the first paint. Best-effort: failures are swallowed.
    // [REQ-GUIDECACHE-PREFETCH-001]
    try {
      const prefetchStartMs: number = fetchEndMs;
      const prefetchEndMs: number =
        fetchEndMs + GUIDE_INITIAL_FUTURE_PREFETCH_MINUTES * 60 * 1000;
      await cache.fetchRange(
        timeWindow.visibleStart,
        channelWindow.channels,
        prefetchStartMs,
        prefetchEndMs
      );
      Log.d(
        TAG,
        "loadGuide — future prefetch complete start=" +
          String(prefetchStartMs) +
          " end=" +
          String(prefetchEndMs)
      );
    } catch (error: unknown) {
      Log.e(TAG, "loadGuide — future prefetch failed", error);
    }
  }
);

/**
 * Fetches guide rows (program data) for the given channels within the
 * specified time window.
 *
 * Populates the appropriate time-window bucket (past, current, or future)
 * in the guide rows state based on the payload.
 */
export const loadGuideRows = createAsyncThunk<
  TGridGuideRow[],
  LoadGuideRowsPayload,
  { rejectValue: string }
>(
  "guide/loadGuideRows",
  async (payload: LoadGuideRowsPayload, { rejectWithValue }) => {
    try {
      Log.i(
        TAG,
        "loadGuideRows() - Fetching rows for " +
          payload.channels.length +
          " channels"
      );

      // Route through the sliding-window cache: covered windows are served from
      // memory (no network), and concurrent identical requests are de-duplicated.
      const rows: TGridGuideRow[] = await GuideCache.getInstance().fetch(
        payload.windowStartTime,
        payload.channels
      );

      Log.d(TAG, "loadGuideRows() SUCCESS - loaded " + rows.length + " rows");

      return rows;
    } catch (error: unknown) {
      Log.e(TAG, "loadGuideRows() - unexpected error", error);

      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to load guide rows");
    }
  }
);

/**
/**
 * Determines whether a content action advertises catch-up playback.
 *
 * Catch-up is only available when the offer exposes a watch-from-provider
 * action whose kernel reports `isCatchUp` together with a SOCU offer id (used
 * by the playback pipeline to fetch the watch action details).
 *
 * @param action Content action from the offer actions response.
 * @returns True when the action represents a playable catch-up offer.
 */
const isCatchupWatchAction = (action: TAction): boolean => {
  if (action.actionType !== ActionType.WatchFromProviderAction) {
    return false;
  }
  const kernel: unknown = action.kernel;
  if (kernel === undefined || kernel === null || typeof kernel !== "object") {
    return false;
  }
  const kernelRecord = kernel as Record<string, unknown>;
  return kernelRecord.isCatchUp === true;
};

/**
 * Resolves catch-up availability for an ended program by inspecting the live
 * offer actions response. The guide grid `isCatchup` hint is advisory only —
 * not every past program is catch-up enabled, so the actions service is the
 * authoritative source before launching the player. The actions are always
 * fetched fresh (forced) so a stale cache never triggers catch-up playback.
 *
 * @param offerId Offer id of the focused guide program cell.
 * @returns True when the actions response advertises a catch-up watch action.
 */
const isCatchupSupportedByActions = async (
  offerId: string
): Promise<boolean> => {
  try {
    const actionsParams: TActionsParams = {
      offerId,
      groups: [ActionGroup.Watch]
    };
    Log.i(
      TAG,
      "isCatchupSupportedByActions() - fetching fresh offer actions for offerId: " +
        offerId
    );
    const response: TCustomActionList =
      await new ActionsInteractor().fetchOfferActions(actionsParams, true);
    const actions: Array<TAction> | undefined = response.actions;
    if (actions === undefined || actions === null) {
      return false;
    }
    return actions.some(isCatchupWatchAction);
  } catch (error: unknown) {
    Log.e(TAG, "isCatchupSupportedByActions()", error);
    return false;
  }
};

/**
 * Whether playback can start for this channel right now.
 *
 * Two independent reasons to refuse, neither of which covers the other:
 *
 * - **Not tunable at all.** AppLinear channels play inside a partner app and
 *   this app does not deep-link out. A permanent property of the channel.
 * - **Not classifiable yet.** Without the provider's configuration a TiVo+
 *   channel is indistinguishable from ordinary linear, and tuning it as
 *   linear would send an unresolved `TIVOPLUS_*` template to the CDN. A
 *   transient state that clears once configuration loads.
 *
 * They are kept apart here because they log differently and mean different
 * things to anyone reading a device log — the first will never succeed, the
 * second is worth retrying. Note that {@link hasProviderConfiguration} passes
 * AppLinear channels through untouched (they are not `IpStream`), so the
 * tunability check is doing work nothing else does.
 */
const canStartPlaybackForChannel = (channel: TChannel): boolean => {
  if (!ChannelsInteractor.isTunableChannel(channel)) {
    Log.i(
      TAG,
      "canStartPlaybackForChannel() - channel " +
        String(channel.channelNumber) +
        " is not tunable by this app, skipping"
    );
    return false;
  }

  return true;
};

/**
 * Starts playback for the currently focused guide program cell.
 *
 * - Currently airing programs (startTime <= now < endTime) start LIVE playback.
 * - Ended programs start catch-up (SOCU) playback when the offer actions
 *   response advertises a catch-up watch action. There is no resume overlay in
 *   this app, so catch-up always starts as CATCHUP_RESUME.
 * - Everything else is ignored — logs a message and returns false.
 *
 * Uses the focused channel from Redux state to build an IPlayable and
 * delegates to VideoPlaybackController for actual stream start.
 */
export const startGuidePlayback = createAsyncThunk<
  boolean,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "guide/startGuidePlayback",
  async (_: void, { getState, rejectWithValue }) => {
    try {
      const state: UnityRootState = getState();
      const focusedChannel: TChannel | null = selectFocusedGuideChannel(state);
      const focusedCell: TGuideCell | null = selectFocusedGuideCell(state);

      if (focusedCell === null) {
        Log.i(TAG, "startGuidePlayback() - No focused program cell");
        return false;
      }

      if (selectIsFocusedGuideCellPlaceholder(state)) {
        Log.i(
          TAG,
          "startGuidePlayback() - Focused cell is a loading placeholder"
        );
        return false;
      }

      if (focusedChannel === null) {
        Log.w(TAG, "startGuidePlayback() - No focused channel available");
        return false;
      }

      if (focusedChannel.jumpChannelInfo !== undefined) {
        Log.i(TAG, "Jump channel selected");
        // TODO(guide-ott-launch): `ottAppLaunch` (guide logging.md §8) is not
        // implemented here. Launching the third-party app itself has no
        // existing primitive anywhere in this codebase to hang the event off
        // of — no TV-platform bridge (Tizen/webOS/VegaOS intent, etc.), only
        // an unrelated mobile-browser App Store fallback for a different
        // platform/purpose (mobileLandingDialogSlice.ts). The spec's
        // `ott_package` fields also don't map cleanly onto TChannel:
        // package_id/package_name come from jumpChannelInfo.appLaunchInfo/
        // metadata, but provider_id and app_type have no data source (the
        // closest candidate, jumpChannelInfo.metadata.logoPartnerId, is
        // documented as logo-lookup-only). Revisit once the actual app-launch
        // mechanism is designed for the target platform(s).
        return false;
      }

      // contentSelected — fired for every genuine program-cell selection,
      // regardless of whether playback actually starts below.
      GuideAnalyticsLogger.logContentSelected({
        list_name: GUIDE_LIST_NAME,
        list_position: String(
          selectEffectiveGuideHighlightVerticalPosition(state)
        ),
        tile_position: String(
          selectEffectiveGuideHighlightHorizontalPosition(state)
        ),
        ...buildGuidePgdFields(
          focusedCell,
          focusedChannel,
          selectGuidePreviewData(state),
          selectGuidePreviewDataOfferId(state)
        )
      });

      if (!canStartPlaybackForChannel(focusedChannel)) {
        return false;
      }

      const nowMs: number = Date.now();
      const startMs: number = focusedCell.startTime.getTime();
      const endMs: number = startMs + focusedCell.duration * 1000;
      // When the guide-rows service has failed, the focused cell is a
      // "Select to tune to channel" fallback rather than real program data —
      // there is no airing window to validate against, so tuning is always
      // allowed for the focused channel (the channel response carries the
      // live playback URI).
      const isCurrentlyAiring: boolean =
        state.guide.isGuideRowsFailed || (startMs <= nowMs && nowMs < endMs);
      const hasEnded: boolean = endMs <= nowMs;

      if (isCurrentlyAiring) {
        Log.i(
          TAG,
          "startGuidePlayback() - Starting live playback for channel: " +
            focusedChannel.channelNumber
        );
        DiagnosticLogger.log(
          "guide_playback",
          "guide playback route: live",
          DiagnosticSeverity.Info,
          { route: "live" }
        );

        const livePlayable: IPlayable = {
          channel: focusedChannel,
          playbackContentType: PlaybackContentType.LIVE
        };
        VideoPlaybackController.getInstance().startPlayback(livePlayable);
        return true;
      }

      if (hasEnded) {
        // Checked before the offer-actions fetch rather than after: this path
        // ignores the SOCU selectors and asks the server directly, so a
        // catch-up action advertised for a TiVo+ programme would otherwise
        // launch playback the player then has to refuse.
        if (ChannelsInteractor.isTivoPlusChannel(focusedChannel)) {
          Log.i(
            TAG,
            "startGuidePlayback() - TiVo+ channel has no catch-up, skipping"
          );
          return false;
        }

        const offerId: string = focusedCell.offerId;
        if (offerId.length === 0) {
          Log.i(
            TAG,
            "startGuidePlayback() - Ended program has no offerId, skipping"
          );
          return false;
        }

        const catchupSupported: boolean =
          await isCatchupSupportedByActions(offerId);
        if (!catchupSupported) {
          Log.i(
            TAG,
            "startGuidePlayback() - Program does not support catch-up, skipping"
          );
          return false;
        }

        Log.i(
          TAG,
          "startGuidePlayback() - Starting catch-up playback for offerId: " +
            offerId
        );
        DiagnosticLogger.log(
          "guide_playback",
          "guide playback route: catchup_resume",
          DiagnosticSeverity.Info,
          { route: "catchup_resume" }
        );

        const catchupPlayable: IPlayable = {
          channel: focusedChannel,
          playbackContentType: PlaybackContentType.CATCHUP_RESUME,
          offerId
        };
        VideoPlaybackController.getInstance().startPlayback(catchupPlayable);
        return true;
      }

      Log.i(
        TAG,
        "startGuidePlayback() - Program is not airing and not catch-up eligible, skipping"
      );
      return false;
    } catch (error: unknown) {
      const message: string =
        error instanceof Error
          ? error.message
          : "Failed to start guide playback";
      Log.e(TAG, "startGuidePlayback()", error);
      return rejectWithValue(message);
    }
  }
);

/**
 * Reverts a channel's favorite state after a failed persist — but only if
 * the channel's current in-memory state still matches the state that was
 * sent to the API (§2.5). If the user re-toggled the channel during the
 * network round-trip, the newer intent takes precedence and this is a no-op
 * (edge case #5).
 *
 * Also checks whether the revert dropped the Favorites-filter's favorite
 * count to zero (auto-disable, §3.4) and persists the resulting filter
 * state when it changed.
 *
 * @param dispatch Thunk dispatch used to apply the rollback.
 * @param getState Thunk getState used to read current state for the guard.
 * @param channelId Channel identifier to look up.
 * @param expectedIsFavorite The favorite state that was sent to the API.
 */
const rollbackFavoriteIfStillMatches = (
  dispatch: (action: UnknownAction) => unknown,
  getState: () => UnityRootState,
  channelId: string,
  expectedIsFavorite: boolean
): void => {
  const state: UnityRootState = getState();
  const channel: TChannel | undefined = state.guide.channels.find(
    (ch: TChannel): boolean => ch.channelId === channelId
  );

  if (channel === undefined || channel.isFavorite !== expectedIsFavorite) {
    Log.i(
      TAG,
      "rollbackFavoriteIfStillMatches() - skipping stale rollback — channelId: " +
        channelId
    );
    return;
  }

  const originalIsFavorite: boolean = !expectedIsFavorite;

  const interactor: ChannelsInteractor = new ChannelsInteractor();
  interactor
    .updateFavoriteChannel(channel, originalIsFavorite)
    .catch((error: unknown): void => {
      Log.e(
        TAG,
        "rollbackFavoriteIfStillMatches() - updateFavoriteChannel failed",
        error
      );
    })
    // The failed toggle may have been the one that made `hasFavoriteChannels`
    // false (disabling Favorites) — re-derive it from the full channel list
    // now that the cache is reverted.
    .then(() => interactor.fetchAllChannels())
    .then((result): void => {
      dispatch(setHasFavoriteChannels(hasAnyFavoriteChannel(result.channels)));
    })
    .catch((error: unknown): void => {
      Log.w(
        TAG,
        "rollbackFavoriteIfStillMatches() - favorites recheck failed",
        error
      );
    });
  dispatch(
    guideSlice.actions.revertFavoriteChannel({
      channelId,
      isFavorite: originalIsFavorite
    })
  );
  if (originalIsFavorite) {
    // Restoring a favorite guarantees at least one exists — re-enable
    // Favorites immediately rather than waiting for the cache re-read.
    dispatch(setHasFavoriteChannels(true));
  }
};

/**
 * Fires the channel-settings API call for a debounced favorite toggle and
 * applies conditional rollback on failure (§2.4, §2.5).
 *
 * A version mismatch means a newer toggle on the same channel has already
 * superseded this job (guards against a scheduling race with
 * scheduleFavoritePersist); the stale call is skipped entirely.
 */
const persistFavoriteChange = (
  dispatch: (action: UnknownAction) => unknown,
  getState: () => UnityRootState,
  channelId: string,
  channelNumber: string,
  desiredIsFavorite: boolean,
  version: number
): void => {
  const job: FavoritePersistJob | undefined =
    favoritePersistJobs.get(channelId);
  if (job === undefined || job.version !== version) {
    Log.i(
      TAG,
      "persistFavoriteChange() - stale job skipped — channelId: " + channelId
    );
    return;
  }
  favoritePersistJobs.delete(channelId);

  const interactor: ChannelsInteractor = new ChannelsInteractor();
  interactor
    .setChannelSettings(channelNumber, desiredIsFavorite)
    .then((success: boolean) => {
      if (!success) {
        Log.w(
          TAG,
          "persistFavoriteChange() - setChannelSettings returned false, reverting — channelId: " +
            channelId
        );
        OperationalLogger.logError({
          errorCode: GUIDE_ERROR_CODE.FAVORITE_TOGGLE_FAILED,
          errorMessage:
            "Failed to update favourite channel: setChannelSettings returned false"
        });
        rollbackFavoriteIfStillMatches(
          dispatch,
          getState,
          channelId,
          desiredIsFavorite
        );
      }
    })
    .catch((error: unknown) => {
      Log.e(
        TAG,
        "persistFavoriteChange() - setChannelSettings failed, reverting — channelId: " +
          channelId,
        error
      );
      if (!isCancellation(error)) {
        OperationalLogger.logError({
          errorCode: GuideErrorMapper.toCompositeCode(
            GUIDE_ERROR_CODE.FAVORITE_TOGGLE_FAILED,
            error
          ),
          errorMessage: GuideErrorMapper.toErrorMessage(
            error,
            "Failed to update favourite channel"
          )
        });
      }
      rollbackFavoriteIfStillMatches(
        dispatch,
        getState,
        channelId,
        desiredIsFavorite
      );
    });
};

/**
 * Schedules a debounced persist of a channel's favorite state to the
 * channel-settings API (§2.3).
 *
 * Per-channel scope: each channel has its own independent timer — toggling
 * channel A never delays or cancels channel B's pending persist (edge case
 * #2). A new call for the same channel cancels the previous pending timer
 * and bumps the version counter so a stale in-flight callback for a
 * superseded job is ignored (final-state-wins, edge case #1).
 *
 * @param dispatch Thunk dispatch used to apply/rollback Redux state.
 * @param getState Thunk getState used for the rollback guard.
 * @param channelId Channel identifier used to key the debounce map.
 * @param channelNumber Channel number sent to the settings API.
 * @param desiredIsFavorite The favorite state to persist once the debounce
 *   window elapses.
 */
const scheduleFavoritePersist = (
  dispatch: (action: UnknownAction) => unknown,
  getState: () => UnityRootState,
  channelId: string,
  channelNumber: string,
  desiredIsFavorite: boolean
): void => {
  const existingJob: FavoritePersistJob | undefined =
    favoritePersistJobs.get(channelId);
  if (existingJob !== undefined) {
    clearTimeout(existingJob.timer);
  }
  const version: number =
    existingJob !== undefined ? existingJob.version + 1 : 1;

  const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
    persistFavoriteChange(
      dispatch,
      getState,
      channelId,
      channelNumber,
      desiredIsFavorite,
      version
    );
  }, FAVORITE_PERSIST_DEBOUNCE_MS);

  favoritePersistJobs.set(channelId, { timer, version });
};

/**
 * Toggles the `isFavorite` flag on the currently focused guide channel.
 *
 * Only operates when horizontal focus is on the channel cell (horizontalPosition = 0).
 * Applies the optimistic update immediately (§2.2), persists the change to
 * IndexedDB via ChannelsService, then schedules a debounced (§2.3) settings
 * API call with conditional rollback on failure (§2.5).
 */
export const toggleFavoriteChannel = createAsyncThunk<
  TChannel,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "guide/toggleFavoriteChannel",
  async (_: void, { getState, dispatch, rejectWithValue }) => {
    try {
      const state: UnityRootState = getState();
      const focusedChannel: TChannel | null = selectFocusedGuideChannel(state);

      if (focusedChannel === null) {
        Log.i(TAG, "toggleFavoriteChannel() - No focused channel");
        return rejectWithValue("No focused channel");
      }

      const newIsFavorite: boolean = focusedChannel.isFavorite !== true;
      const channelId: string =
        focusedChannel.channelId !== undefined ? focusedChannel.channelId : "";
      const channelNumber: string =
        focusedChannel.channelNumber !== undefined
          ? focusedChannel.channelNumber
          : "";

      const interactor: ChannelsInteractor = new ChannelsInteractor();

      // Update local Storage cache immediately (awaited) so the UI reflects
      // the change without waiting for the network round-trip.
      await interactor.updateFavoriteChannel(focusedChannel, newIsFavorite);

      // Optimistic update (§2.2): applied via a direct reducer dispatch
      // (rather than relying solely on the auto-dispatched `.fulfilled`
      // action) so this thunk can inspect the resulting Redux state right
      // away — needed to detect a Favorites-filter auto-disable (§3.4).
      dispatch(
        guideSlice.actions._applyFavoriteChannelUpdate({
          channelId,
          stationId: focusedChannel.stationId,
          isFavorite: newIsFavorite
        })
      );

      // The full channel list (not the possibly filter-narrowed
      // `state.channels`) decides whether any favorite remains. When the last
      // one is gone, drop an active Favorites selection silently and let the
      // filter apply flow restore the unfiltered grid.
      const allChannels: TChannel[] = (await interactor.fetchAllChannels())
        .channels;
      dispatch(setHasFavoriteChannels(hasAnyFavoriteChannel(allChannels)));
      const afterUpdate: GuideState = (getState() as GuideThunkState).guide;
      if (!afterUpdate.hasFavoriteChannels) {
        if (isFavoritesFilterSelected(afterUpdate)) {
          dispatch(
            removeGuideFilterSelection(getFavoriteFilterId(afterUpdate))
          );
          // Only Favorites changed — category ids are unaffected, so don't
          // re-call /v2/guideChannelIds.
          scheduleGuideFilterApply(dispatch, getState, true).catch(
            (applyError: unknown): void =>
              Log.e(
                TAG,
                "toggleFavoriteChannel() - filter apply failed",
                applyError
              )
          );
        }
        // Sidebar focus may sit on the now-disabled Favorites item.
        if (afterUpdate.isSidebarFocused) {
          dispatch(setIsSidebarFocused(true));
        }
      }

      // Per-channel debounced persistence (§2.3): schedules the settings API
      // call after FAVORITE_PERSIST_DEBOUNCE_MS, cancelling any prior
      // pending job for this channel (final-state-wins).
      scheduleFavoritePersist(
        dispatch,
        getState,
        channelId,
        channelNumber,
        newIsFavorite
      );

      Log.d(
        TAG,
        "toggleFavoriteChannel() SUCCESS — channelId: " +
          focusedChannel.channelId +
          ", isFavorite: " +
          String(newIsFavorite)
      );
      DiagnosticLogger.log(
        "guide_filter",
        "channel " + channelNumber + " favorite toggled",
        DiagnosticSeverity.Info,
        { is_favorite: String(newIsFavorite) }
      );
      GuideAnalyticsLogger.logItemSelected({
        list_name: "ChannelRow",
        list_position: String(
          selectEffectiveGuideHighlightVerticalPosition(state)
        ),
        tile_id: channelId,
        is_favorite: String(newIsFavorite)
      });

      return { ...focusedChannel, isFavorite: newIsFavorite };
    } catch (error: unknown) {
      Log.e(TAG, "toggleFavoriteChannel()", error);
      const message: string = channelsErrorMessage(
        error,
        "Failed to toggle favourite"
      );
      const errorCode: string = channelsErrorCode(
        error,
        GUIDE_ERROR_CODE.FAVORITE_TOGGLE_FAILED
      );
      const errorType: string = errorTypeName(error);
      DiagnosticLogger.log(
        "guide_filter",
        "toggleFavoriteChannel failed",
        DiagnosticSeverity.Error,
        { error_code: errorCode, error_type: errorType }
      );
      OperationalLogger.logError({
        errorCode: GuideErrorMapper.toCompositeCode(
          GUIDE_ERROR_CODE.FAVORITE_TOGGLE_FAILED,
          error
        ),
        errorMessage: GuideErrorMapper.toErrorMessage(
          error,
          "Failed to toggle favourite"
        )
      });
      return rejectWithValue(message);
    }
  }
);

/**
 * contentSelected + viewedContentDetails for the future-program branch of
 * `handleKeyOk` — a highlighted program that has not started yet opens
 * Content Detail instead of tuning. Reads focus/position state itself via
 * `getState()` so the view needs no selectors beyond what it already
 * subscribes to for the future-program check itself.
 */
export const logGuideFutureProgramSelection = createAsyncThunk<
  void,
  void,
  { state: UnityRootState }
>("guide/logGuideFutureProgramSelection", (_: void, { getState }) => {
  const state: UnityRootState = getState();
  const focusedCell: TGuideCell | null = selectFocusedGuideCell(state);
  if (focusedCell === null) {
    return;
  }

  const fields: Partial<TAnalyticsFields> = {
    list_name: GUIDE_LIST_NAME,
    list_position: String(selectEffectiveGuideHighlightVerticalPosition(state)),
    tile_position: String(
      selectEffectiveGuideHighlightHorizontalPosition(state)
    ),
    ...buildGuidePgdFields(
      focusedCell,
      selectFocusedGuideChannel(state),
      selectGuidePreviewData(state),
      selectGuidePreviewDataOfferId(state)
    )
  };
  GuideAnalyticsLogger.logContentSelected(fields);
  GuideAnalyticsLogger.logViewedContentDetails(fields);
});

/**
 * Captures the screen the user was on immediately before entering Guide.
 * Dispatched once at mount; the payload creator runs synchronously (no
 * `await` before its first read), so `guideEntryScreenId` is set before
 * `dispatch()` returns — safe to call from a mount effect with no race
 * against `logGuideViewedScreen` at unmount.
 */
export const captureGuideEntryScreen = createAsyncThunk<
  void,
  void,
  { state: UnityRootState }
>("guide/captureGuideEntryScreen", (_: void, { getState }) => {
  guideEntryScreenId = selectPreviousUrl(getState());
});

/**
 * viewedScreen — fired on exit only (unmount), per spec, using the
 * previous-screen id captured at mount by {@link captureGuideEntryScreen}
 * (reading `selectPreviousUrl` fresh here would return Guide's own url,
 * since NavigationInterceptor has already advanced it by unmount time).
 * `timeOnScreenMs` is passed in because only the view knows its own mount
 * timestamp — that is inherently component-lifecycle state, not Redux state.
 */
export const logGuideViewedScreen = createAsyncThunk<void, number>(
  "guide/logGuideViewedScreen",
  (timeOnScreenMs: number) => {
    GuideAnalyticsLogger.logViewedScreen({
      previous_screen_id: guideEntryScreenId,
      time_on_screen: String(timeOnScreenMs)
    });
  }
);

/**
 * Unified OK-key handler for the guide screen.
 *
 * Routes the OK action based on focus state:
 * - Sidebar focused (isSidebarFocused = true): applies the focused filter category.
 *   For FAVORITE: filters the grid to favorite channels only, positions focus on
 *   the first program of the first favorite channel at the 3rd row.
 *   Works for both current guide (entered from channel cell, hPos 0) and past guide
 *   (entered from the last program cell).
 * - Channel cell (horizontalPosition = 0, sidebar not focused): toggles the focused
 *   channel's isFavorite.
 * - Program cell (horizontalPosition >= 1): starts live playback if the program is
 *   airing, or catch-up (CATCHUP_RESUME) playback if the program has ended and
 *   supports catch-up.
 *
 * Returns true when the caller should navigate to the player, false otherwise.
 * Encapsulates the branching logic so the view layer dispatches a single action
 * with no business logic of its own.
 */
export const handleGuideKeyOk = createAsyncThunk<
  boolean,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "guide/handleGuideKeyOk",
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    try {
      const state: UnityRootState = getState();
      const effectiveHPos: number =
        selectEffectiveGuideHighlightHorizontalPosition(state);

      if (state.guide.isSidebarFocused) {
        const focusedFilter: TGuideFilterItem | undefined =
          state.guide.filterItems[state.guide.sidebarFocusIndex];
        const focusedFilterId: string =
          focusedFilter !== null && focusedFilter !== undefined
            ? focusedFilter.id
            : "none";
        Log.i(
          TAG,
          "handleGuideKeyOk() - Sidebar filter selection: sidebarFocusIndex=" +
            state.guide.sidebarFocusIndex +
            " focusedFilter=" +
            focusedFilterId
        );
        if (
          focusedFilter !== null &&
          focusedFilter !== undefined &&
          !isFilterItemDisabled(state.guide, focusedFilter)
        ) {
          await dispatch(toggleGuideFilter(focusedFilter.id));
        }
        return false;
      }

      if (effectiveHPos === 0) {
        await dispatch(toggleFavoriteChannel());
        return false;
      }

      const result = await dispatch(startGuidePlayback());
      if (startGuidePlayback.fulfilled.match(result)) {
        return result.payload;
      }
      return false;
    } catch (error: unknown) {
      Log.e(TAG, "handleGuideKeyOk()", error);
      const message: string =
        error instanceof Error ? error.message : "handleGuideKeyOk failed";
      return rejectWithValue(message);
    }
  }
);

/**
 * Assigns fetched rows into the correct bucket of GuideRowsState.
 *
 * @param guideRows Mutable guide rows draft.
 * @param timeWindow Target time-window bucket.
 * @param rows Fetched guide rows to store.
 */
const setRowsForWindow = (
  guideRows: Draft<GuideRowsState>,
  timeWindow: GuideTimeWindow,
  rows: TGridGuideRow[]
): void => {
  if (timeWindow === GuideTimeWindow.PAST) {
    guideRows.pastRows = rows;
  } else if (timeWindow === GuideTimeWindow.FUTURE) {
    guideRows.futureRows = rows;
  } else {
    guideRows.currentRows = rows;
  }
};

/**
 * Returns the mutable bucket array for a given time window.
 *
 * @param guideRows Mutable guide rows draft.
 * @param timeWindow Target time-window bucket.
 * @returns The bucket array reference for the requested window.
 */
const pickRowsBucket = (
  guideRows: Draft<GuideRowsState>,
  timeWindow: GuideTimeWindow
): Draft<TGridGuideRow[]> => {
  if (timeWindow === GuideTimeWindow.PAST) {
    return guideRows.pastRows;
  }
  if (timeWindow === GuideTimeWindow.FUTURE) {
    return guideRows.futureRows;
  }
  return guideRows.currentRows;
};

/**
 * Merges fetched rows into the correct bucket, placing each row at the absolute
 * channel index derived from the window's start index. Rows arrive in
 * window-channel order, so row N maps to absolute index
 * (startGlobalIndex + N) mod channelCount. Position-based placement (rather
 * than stationId lookup) keeps duplicate or missing stationIds from collapsing
 * multiple channels onto the same slot, which would leave the others blank.
 * Channels outside the cached window keep their existing (or empty) slot.
 *
 * @param guideRows Mutable guide rows draft.
 * @param channelCount Total number of channels (modulo for wrap-around).
 * @param timeWindow Target time-window bucket.
 * @param rows Fetched guide rows, in window-channel order.
 * @param startGlobalIndex Absolute index of the first windowed channel.
 */
const mergeRowsForWindow = (
  guideRows: Draft<GuideRowsState>,
  channelCount: number,
  timeWindow: GuideTimeWindow,
  rows: TGridGuideRow[],
  startGlobalIndex: number
): void => {
  if (channelCount === 0 || startGlobalIndex < 0) {
    return;
  }

  const bucket: Draft<TGridGuideRow[]> = pickRowsBucket(guideRows, timeWindow);

  for (let index: number = 0; index < rows.length; index++) {
    const absoluteIndex: number =
      (((startGlobalIndex + index) % channelCount) + channelCount) %
      channelCount;
    bucket[absoluteIndex] = rows[index];
  }
};

/**
 * Redux slice for the Guide (EPG) feature.
 *
 * Manages channel loading/error state and focus routing for the
 * electronic program guide grid screen. Guide rows (program data)
 * are stored as a nested sub-object without separate loading/error tracking.
 */
const guideSlice = createSlice({
  name: "guide",
  initialState: INITIAL_STATE,
  reducers: {
    /** Sets the loading flag while guide data is being fetched. */
    setGuideLoading(state, action: PayloadAction<boolean>): void {
      state.isLoading = action.payload;
    },

    /** Sets the navigation-engine mode (see GuideNavMode). */
    setGuideNavMode(state, action: PayloadAction<GuideNavMode>): void {
      state.navMode = action.payload;
    },

    /** Sets whether loading cells have timed out (LOADING → TBA). */
    setGuideLoadingTimedOut(state, action: PayloadAction<boolean>): void {
      state.guideLoadingTimedOut = action.payload;
    },

    /** Records an error message from the last failed guide data fetch. */
    setGuideError(state, action: PayloadAction<string>): void {
      state.error = action.payload;
    },

    /**
     * Records whether the guide-rows (EPG program schedule) service has
     * failed to load data for the current window. Channels are unaffected.
     */
    setGuideRowsFailed(state, action: PayloadAction<boolean>): void {
      state.isGuideRowsFailed = action.payload;
    },

    /**
     * Handles a guide-rows service failure encountered while browsing the
     * grid: clears all cached/rendered guide rows and snaps the visible
     * window back to the current time so the user is never left stranded in
     * a past/future window with no program data to show. Channel focus
     * (verticalPosition / channelScrollOffset) is left untouched.
     */
    applyGuideRowsFailure(state, action: PayloadAction<DateTime>): void {
      const now: DateTime = action.payload;
      state.guideRows = INITIAL_GUIDE_ROWS_STATE;
      state.currentWindowStartTime = now;
      state.pastWindowStartTime = now.subtractMinutes(
        GUIDE_WINDOW_DURATION_MINUTES
      );
      state.futureWindowStartTime = now.addMinutes(
        GUIDE_WINDOW_DURATION_MINUTES
      );
      state.isPastGuide = false;
      state.horizontalPosition = 1;
      state.highlightAnchorTimeMs = 0;
      state.isGuideRowsFailed = true;
    },

    /** Sets the current guide window start time as a DateTime instance. */
    setCurrentWindowStartTime(state, action: PayloadAction<DateTime>): void {
      state.currentWindowStartTime = action.payload;
      // Re-anchor the session start on every call (guide mount) so navigation
      // limits are always computed relative to the live time the guide was
      // (re-)entered at, not a timestamp cached from a much earlier session.
      state.guideSessionStartMs = action.payload.getTime();
    },

    /** Sets whether the guide is displaying past program data. */
    setIsPastGuide(state, action: PayloadAction<boolean>): void {
      state.isPastGuide = action.payload;
    },

    /** Clears guide rows and resets them to the initial state. */
    resetGuideRows(state): void {
      state.guideRows = INITIAL_GUIDE_ROWS_STATE;
    },

    /**
     * Clears the past/future browsing flags left over from a prior Guide
     * session so a fresh entry into Guide always lands on the live/current
     * position, regardless of what was previously being viewed. Channel
     * focus (verticalPosition / channelScrollOffset) is left untouched, as
     * that is intentionally re-derived from the last tuned channel on mount.
     */
    resetGuideToLivePosition(state): void {
      state.isPastGuide = false;
      state.horizontalPosition = 1;
      state.highlightAnchorTimeMs = 0;
    },

    /** Resets the entire guide slice to its initial state. */
    resetGuideState(): GuideState {
      return INITIAL_STATE;
    },

    /** Directly sets the channel scroll offset (index of the top visible channel). */
    setChannelScrollOffset(state, action: PayloadAction<number>): void {
      state.channelScrollOffset = action.payload;
    },

    /** Directly sets the vertical (row) focus position. */
    setVerticalPosition(state, action: PayloadAction<number>): void {
      state.verticalPosition = action.payload;
    },

    /**
     * Directly sets guide rows for a given time window.
     * Used by the navigation thunks to hydrate Redux from the warm cache
     * without triggering a network fetch.
     */
    setGuideRowsDirect(
      state,
      action: PayloadAction<{
        rows: TGridGuideRow[];
        timeWindow: GuideTimeWindow;
      }>
    ): void {
      setRowsForWindow(
        state.guideRows,
        action.payload.timeWindow,
        action.payload.rows
      );
    },

    /** Updates the anchor time of the pre-fetched future window. */
    setFutureWindowStartTime(state, action: PayloadAction<DateTime>): void {
      state.futureWindowStartTime = action.payload;
    },

    /** Updates the anchor time of the pre-fetched past window. */
    setPastWindowStartTime(state, action: PayloadAction<DateTime>): void {
      state.pastWindowStartTime = action.payload;
    },

    /**
     * Shifts the time axis one step forward (30 min).
     * Promotes futureRows → currentRows → pastRows.
     * No-ops if the future boundary (now + 14 days) would be exceeded.
     */
    shiftTimeAxisForward(state): void {
      if (applyShiftForward(state)) {
        state.windowLoadGeneration = state.windowLoadGeneration + 1;
      }
    },

    /**
     * Shifts the time axis one step backward (30 min).
     * Promotes pastRows → currentRows → futureRows.
     * Sets isPastGuide = true on first backward shift.
     * No-ops if the past boundary (now − epgLookBackHours) would be exceeded.
     */
    shiftTimeAxisBackward(state, action: PayloadAction<number>): void {
      if (applyShiftBackward(state, action.payload)) {
        state.windowLoadGeneration = state.windowLoadGeneration + 1;
      }
    },

    /**
     * Advances all three window anchors to a new boundary epoch and resets
     * horizontal focus to the first program slot.
     * Dispatched exclusively by the autoRefreshGuideWindow thunk after the
     * guards confirm the user is viewing the live On Now window.
     *
     * @param newBoundaryMs New boundary epoch milliseconds.
     */
    advanceWindowForAutoRefresh(state, action: PayloadAction<number>): void {
      applyAutoRefreshAdvance(state, action.payload);
    },

    /**
     * Refreshes guideSessionStartMs to the given wall-clock boundary without
     * moving the visible window. Dispatched by autoRefreshGuideWindow while
     * the user is browsing away from the On Now window (past or future), so
     * the live-time anchor never goes stale across a long-idle guide session.
     */
    refreshGuideSessionAnchor(state, action: PayloadAction<number>): void {
      applyGuideSessionAnchorRefresh(state, action.payload);
    },

    /** Sets the focused vertical guideHighlight position in the grid. */
    setGuideHighlightVerticalPosition(
      state,
      action: PayloadAction<number>
    ): void {
      state.verticalPosition = action.payload;
    },

    /** Sets the focused horizontal guideHighlight position in the row. */
    setGuideHighlightHorizontalPosition(
      state,
      action: PayloadAction<number>
    ): void {
      state.horizontalPosition = action.payload;
    },

    /**
     * Jumps grid focus directly to an arbitrary visible row/column — e.g. a
     * mouse hover over a cell — as opposed to the relative ±1 stepping the
     * navigateGuideUp/Down/Left/Right thunks perform. Resets
     * highlightAnchorTimeMs so it lazily re-seeds from this cell on the next
     * real UP/DOWN press, rather than resolving against a stale time anchor.
     */
    setGuideGridFocus(
      state,
      action: PayloadAction<{
        verticalPosition: number;
        horizontalPosition: number;
      }>
    ): void {
      state.verticalPosition = action.payload.verticalPosition;
      state.horizontalPosition = action.payload.horizontalPosition;
      state.isSidebarFocused = false;
      state.highlightAnchorTimeMs = 0;
    },

    /**
     * Focuses a specific filter-sidebar item directly by index — e.g. a
     * mouse hover — as opposed to setIsSidebarFocused(true), which derives
     * the index from the current grid verticalPosition.
     */
    setGuideSidebarFocus(state, action: PayloadAction<number>): void {
      const hovered: TGuideFilterItem | undefined =
        state.filterItems[action.payload];
      if (hovered === undefined || isFilterItemDisabled(state, hovered)) {
        return;
      }
      if (!state.isSidebarFocused) {
        state.preSidebarEntryPosition = {
          verticalPosition: state.verticalPosition,
          horizontalPosition: state.horizontalPosition,
          channelScrollOffset: state.channelScrollOffset
        };
      }
      state.isSidebarFocused = true;
      state.sidebarFocusIndex = action.payload;
      state.sidebarEnteredAtMs = Date.now();
    },

    /**
     * Single-step up movement reducer (internal).
     * The navigateGuideUp thunk dispatches this, then loads rows for any
     * channels newly scrolled into the cached window.
     */
    _navigateGuideUpStep(state): void {
      if (state.isSidebarFocused) {
        state.sidebarFocusIndex = stepEnabledFilterIndex(
          state,
          state.sidebarFocusIndex,
          -1
        );
        return;
      }
      applyNavigateUp(state);
    },

    /**
     * Single-step down movement reducer (internal).
     * The navigateGuideDown thunk dispatches this, then loads rows for any
     * channels newly scrolled into the cached window.
     */
    _navigateGuideDownStep(state): void {
      if (state.isSidebarFocused) {
        state.sidebarFocusIndex = stepEnabledFilterIndex(
          state,
          state.sidebarFocusIndex,
          1
        );
        return;
      }
      applyNavigateDown(state);
    },

    /** Sets whether the filter sidebar icon is currently highlighted. */
    setIsSidebarFocused(state, action: PayloadAction<boolean>): void {
      if (action.payload && !state.isSidebarFocused) {
        state.preSidebarEntryPosition = {
          verticalPosition: state.verticalPosition,
          horizontalPosition: state.horizontalPosition,
          channelScrollOffset: state.channelScrollOffset
        };
      }
      state.isSidebarFocused = action.payload;
      if (action.payload) {
        // Navigate to the nearest filter category based on current vertical position.
        const effectiveVPos: number = Math.min(
          state.verticalPosition,
          GUIDE_VISIBLE_ROW_COUNT - 1
        );
        // Scale over the filters actually shown at once (the sidebar scrolls
        // the rest), not the full list length.
        const maxFilterIndex: number =
          Math.min(
            state.filterItems.length,
            GUIDE_FILTER_SIDEBAR_MAX_VISIBLE_ITEMS
          ) - 1;
        const scaledIndex: number = Math.round(
          (effectiveVPos / (GUIDE_VISIBLE_ROW_COUNT - 1)) * maxFilterIndex
        );
        state.sidebarFocusIndex = nearestEnabledFilterIndex(
          state,
          Math.min(scaledIndex, maxFilterIndex)
        );
        state.sidebarEnteredAtMs = Date.now();
      }
    },

    /**
     * Closes the filter sidebar from a D-pad/BACK exit point (as opposed to
     * the raw `setIsSidebarFocused(false)`, still used as-is for a
     * mouse-click-outside close or a grid-focus jump). When the sidebar was
     * in its *expanded* (multi-select overlay) presentation, restores the
     * grid position captured when the sidebar gained focus
     * (`preSidebarEntryPosition`) — so the user returns to where they were
     * before entering the filter column, rather than wherever the last
     * toggle's anchor step repositioned the grid. Collapsed quick-selects
     * never reach this reducer — they close via `applyGuideFilterToggle`,
     * which intentionally leaves the post-toggle anchor position in place.
     */
    closeGuideFilterSidebar(state): void {
      if (
        state.isFilterSidebarExpanded &&
        state.preSidebarEntryPosition !== null
      ) {
        state.verticalPosition = state.preSidebarEntryPosition.verticalPosition;
        state.horizontalPosition =
          state.preSidebarEntryPosition.horizontalPosition;
        state.channelScrollOffset =
          state.preSidebarEntryPosition.channelScrollOffset;
      }
      state.isSidebarFocused = false;
      state.preSidebarEntryPosition = null;
    },

    /**
     * Bumps the navigation epoch by 1. Dispatched before any load that a newer
     * navigation must be able to supersede (vertical page load and settle), so
     * an in-flight load can detect it is stale and discard its result.
     */
    _incrementWindowLoadGeneration(state): void {
      state.windowLoadGeneration = state.windowLoadGeneration + 1;
    },

    /**
     * Merges fetched rows into the matching time-window bucket, placing each
     * row at the absolute channel index derived from the window's start index.
     * Rows arrive in window-channel order, so position-based placement keeps
     * duplicate or missing stationIds from collapsing channels onto one slot.
     * Indices outside the cached window remain empty (rendered blank by the
     * view), keeping only the sliding window populated in memory.
     */
    mergeGuideRows(
      state,
      action: PayloadAction<{
        rows: TGridGuideRow[];
        timeWindow: GuideTimeWindow;
        startGlobalIndex: number;
      }>
    ): void {
      mergeRowsForWindow(
        state.guideRows,
        state.channels.length,
        action.payload.timeWindow,
        action.payload.rows,
        action.payload.startGlobalIndex
      );

      // Keep horizontal focus anchored to the same air-time as loading cells
      // become real programs, so the highlight never jumps when data arrives.
      if (state.highlightAnchorTimeMs > 0 && state.horizontalPosition > 0) {
        const focusedVisibleRow: number = getEffectiveVerticalPosition(state);
        const resolved: number = findHorizontalPositionForTime(
          state,
          focusedVisibleRow,
          state.highlightAnchorTimeMs
        );
        if (resolved > 0) {
          state.horizontalPosition = resolved;
        }
      }
    },

    /**
     * Toggles a single guide filter (Favorites or a category) in/out of
     * `selectedFilterIds` — the one pure reducer both filter kinds share, so
     * multiple filters can be selected at once (multi-select).
     *
     * Has no zero-favorites guard of its own — `state.channels` may be
     * narrowed by another filter, so it can't answer that. The guard lives
     * in `toggleGuideFilter` via `state.hasFavoriteChannels`.
     *
     * Positioning (`channelScrollOffset`/`verticalPosition`/
     * `horizontalPosition`) is deliberately NOT touched here for a live
     * toggle — `applyGuideFilterAnchor` (dispatched by the `toggleGuideFilter`
     * thunk after this reducer runs) owns all post-toggle positioning for
     * both filter kinds, so there is exactly one place computing target
     * position instead of two divergent ones.
     *
     * Leaves the sidebar focused so the user can keep selecting more filters
     * and moves to a grid cell explicitly.
     *
     * @param action `filterId` — the toggled filter's id.
     */
    applyGuideFilterToggle(
      state,
      action: PayloadAction<{ filterId: string }>
    ): void {
      const { filterId } = action.payload;
      const wasSelected: boolean = state.selectedFilterIds.includes(filterId);

      if (wasSelected) {
        state.selectedFilterIds = state.selectedFilterIds.filter(
          (id: string): boolean => id !== filterId
        );
      } else {
        state.selectedFilterIds.push(filterId);
      }

      // The channel set behind the highlight is about to change (or has just
      // changed) — a stale time anchor from the previous set must not carry
      // over into the repositioned grid.
      state.highlightAnchorTimeMs = 0;

      // The highlight stays on the sidebar after a toggle (collapsed or
      // expanded) — the user moves it to a grid cell explicitly. The anchor
      // reposition now targets a different channel set, so the position
      // captured on sidebar entry is stale and must not be restored later.
      state.preSidebarEntryPosition = null;
    },

    /** Records whether any favorite channel exists in the full channel list. */
    setHasFavoriteChannels(state, action: PayloadAction<boolean>): void {
      state.hasFavoriteChannels = action.payload;
    },

    /** Removes a filter id from the selection (no-op when not selected). */
    removeGuideFilterSelection(state, action: PayloadAction<string>): void {
      state.selectedFilterIds = state.selectedFilterIds.filter(
        (id: string): boolean => id !== action.payload
      );
    },

    /**
     * Records the resolved category-filter channel ids from the last
     * `/v2/guideChannelIds` call, or clears back to `"idle"` when the
     * non-favorite selection is empty (skipping the network call entirely —
     * see `toggleGuideFilter`).
     */
    applyCategoryFilterChannelIds(
      state,
      action: PayloadAction<{
        status: "idle" | "resolved";
        channelIds: string[];
      }>
    ): void {
      state.categoryFilterResolutionStatus = action.payload.status;
      state.categoryFilterChannelIds = action.payload.channelIds;
    },

    /**
     * Marks the last category-filter resolution attempt as failed. The
     * previously resolved ids are discarded (never trusted once stale) —
     * `matchesActiveGuideFilters` only trusts ids when status is `"resolved"`.
     */
    applyCategoryFilterChannelIdsFailed(state): void {
      state.categoryFilterResolutionStatus = "failed";
      state.categoryFilterChannelIds = [];
    },

    /**
     * Replaces `state.channels`/`state.channelCount` outright — dispatched
     * by `refreshGuideChannelsForActiveFilters` with a freshly-fetched
     * channel list already narrowed to the active filter selection (or the
     * full list when nothing is selected). Unlike `loadGuideChannels`'s
     * `.fulfilled` handler (the initial, always-unfiltered load), this is
     * the one place `state.channels` is overwritten *after* mount, so the
     * guide grid always reads its already-filtered channel list directly
     * instead of re-deriving it per render.
     */
    setGuideChannels(
      state,
      action: PayloadAction<{ channels: TChannel[]; channelCount: number }>
    ): void {
      state.channels = action.payload.channels;
      state.channelCount = action.payload.channelCount;
    },

    /** Lifted from GuideFilterSidebar's local hover-dwell expand state. */
    setIsFilterSidebarExpanded(state, action: PayloadAction<boolean>): void {
      state.isFilterSidebarExpanded = action.payload;
    },

    // ── Jump-to-Channel Reducers ──

    /**
     * Appends a single digit to the jump-to-channel buffer and shows the overlay.
     * Length guard is enforced by the caller thunk; reducer trusts the input.
     */
    _jumpAppendDigit(state, action: PayloadAction<string>): void {
      state.jumpToChannel.digits += action.payload;
      state.jumpToChannel.isVisible = true;
      state.jumpToChannel.isError = false;
    },

    /**
     * Switches the overlay to the "Not found" error state.
     * The digit buffer is cleared; overlay remains visible for the error display.
     */
    _jumpSetError(state): void {
      state.jumpToChannel.digits = "";
      state.jumpToChannel.isError = true;
      state.jumpToChannel.isVisible = true;
    },

    /**
     * Resets all jump-to-channel state to initial values.
     * Called after a successful jump, after the error display expires, or
     * when the overlay is dismissed for any reason.
     */
    _jumpReset(state): void {
      state.jumpToChannel.digits = "";
      state.jumpToChannel.isVisible = false;
      state.jumpToChannel.isError = false;
    },

    /**
     * Applies a resolved jump: sets channelScrollOffset and verticalPosition
     * so the target channel appears at the correct row. horizontalPosition
     * (and isPastGuide / the time window) is left untouched so the focus stays
     * on the current program column and time slot the user was already viewing.
     */
    _applyJumpToChannel(state, action: PayloadAction<JumpScrollTarget>): void {
      state.channelScrollOffset = action.payload.channelScrollOffset;
      state.verticalPosition = action.payload.verticalPosition;
      state.highlightAnchorTimeMs = 0;
      state.jumpToChannel.digits = "";
      state.jumpToChannel.isVisible = false;
      state.jumpToChannel.isError = false;
    },

    /**
     * Clears guide offer preview data and resets the loading flag.
     * Dispatched by GuidePreview when focus moves to a channel row or
     * the focused cell has no valid offerId.
     */
    clearGuidePreview(state): void {
      state.previewData = null;
      state.previewDataOfferId = null;
      state.isPreviewLoading = false;
    },

    /**
     * Reverts the isFavorite flag for a channel in Redux state.
     * Dispatched when setChannelSettings API call fails or returns false,
     * to roll back the optimistic update applied by toggleFavoriteChannel.
     */
    revertFavoriteChannel(
      state,
      action: PayloadAction<{ channelId: string; isFavorite: boolean }>
    ): void {
      const { channelId, isFavorite } = action.payload;
      applyFavoriteChannelUpdateToState(
        state,
        channelId,
        undefined,
        isFavorite
      );
    },

    /**
     * Applies the optimistic favorite-toggle update (internal).
     * Dispatched manually by the toggleFavoriteChannel thunk (rather than
     * relying solely on the auto-dispatched `.fulfilled` action) so the
     * thunk can inspect the resulting Redux state immediately afterward
     * (e.g. to detect a Favorites-filter auto-disable and persist it).
     */
    _applyFavoriteChannelUpdate(
      state,
      action: PayloadAction<{
        channelId: string | undefined;
        stationId: string | undefined;
        isFavorite: boolean;
      }>
    ): void {
      const { channelId, stationId, isFavorite } = action.payload;
      applyFavoriteChannelUpdateToState(
        state,
        channelId,
        stationId,
        isFavorite
      );
    },

    /**
     * Single-step left movement reducer (internal).
     * Stalls at hPos 0 — the navigateGuideLeft thunk detects the stall
     * and dispatches shiftTimeAxisBackward.
     */
    _navigateGuideLeftStep(state): void {
      applyHorizontalLeftStep(state);
    },

    /**
     * Single-step right movement reducer (internal).
     * Stalls at max hPos — the navigateGuideRight thunk detects the stall
     * and dispatches shiftTimeAxisForward.
     */
    _navigateGuideRightStep(state): void {
      applyHorizontalRightStep(state);
    }
  },
  extraReducers: (builder) => {
    builder
      // ── Channel thunk handlers ──
      .addCase(loadGuideChannels.pending, (state) => {
        state.isLoading = true;
        state.error = "";
      })
      .addCase(loadGuideChannels.fulfilled, (state, action) => {
        state.isLoading = false;
        state.channels = action.payload.channels;
        state.channelCount = action.payload.channelCount;
        state.error = "";
      })
      .addCase(loadGuideChannels.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload === undefined ? "Unknown error" : action.payload;
      })
      // ── Guide rows thunk handlers ──
      .addCase(loadGuideRows.pending, (_state) => {
        // No loading state tracked for guide rows
        // TODO: Loading state will be done as part of navigation handling
      })
      .addCase(loadGuideRows.fulfilled, (state, action) => {
        setRowsForWindow(
          state.guideRows,
          action.meta.arg.timeWindow,
          action.payload
        );
      })
      .addCase(loadGuideRows.rejected, (_state) => {
        // No error state tracked for guide rows
      })
      // ── Toggle favourite thunk handlers ──
      .addCase(toggleFavoriteChannel.fulfilled, (state, action) => {
        // State is already applied optimistically by the thunk's manual
        // _applyFavoriteChannelUpdate dispatch; re-applying here is an
        // idempotent no-op safety net (§6 idempotency requirement).
        const updatedChannel: TChannel = action.payload;
        applyFavoriteChannelUpdateToState(
          state,
          updatedChannel.channelId,
          updatedChannel.stationId,
          updatedChannel.isFavorite === true
        );
      })
      .addCase(toggleFavoriteChannel.rejected, (_state) => {
        // Intentionally not updating state.error to avoid overwriting the
        // full-screen guide error banner for a transient favourite-toggle failure.
        // The error is already logged inside the thunk's catch block.
      })
      // ── NPVR allow recording thunk handlers ──
      .addCase(fetchGuideNpvrAllowRecording.fulfilled, (state, action) => {
        state.isNpvrRecordingAllowed = action.payload;
      })
      .addCase(fetchGuideNpvrAllowRecording.rejected, (state) => {
        state.isNpvrRecordingAllowed = false;
      })
      // ── Guide offer preview thunk handlers ──
      .addCase(fetchGuideOfferPreview.pending, (state) => {
        state.previewData = null;
        state.previewDataOfferId = null;
        state.isPreviewLoading = true;
      })
      .addCase(fetchGuideOfferPreview.fulfilled, (state, action) => {
        state.previewData = action.payload;
        state.previewDataOfferId = action.meta.arg.offerId;
        state.isPreviewLoading = false;
      })
      .addCase(fetchGuideOfferPreview.rejected, (state, action) => {
        state.isPreviewLoading = false;
        if (action.meta.aborted !== true) {
          state.previewData = null;
          state.previewDataOfferId = null;
        }
      })
      // ── Guide filters thunk handlers ──
      .addCase(loadGuideFilters.fulfilled, (state, action) => {
        state.filterItems = action.payload;
      })
      .addCase(loadGuideFilters.rejected, (state) => {
        state.filterItems = [getGuideFavoritesFallbackFilter()];
      })
      // ── Guide filter selection restore thunk handler ──
      .addCase(restoreGuideFilterSelection.fulfilled, (state, action) => {
        state.selectedFilterIds = action.payload;
      });
  }
});

const { _navigateGuideLeftStep, _navigateGuideRightStep } = guideSlice.actions;

const { _navigateGuideUpStep, _navigateGuideDownStep } = guideSlice.actions;

const { _incrementWindowLoadGeneration } = guideSlice.actions;

const { _jumpAppendDigit, _jumpSetError, _jumpReset, _applyJumpToChannel } =
  guideSlice.actions;

export const {
  setGuideLoading,
  setGuideNavMode,
  setGuideLoadingTimedOut,
  setGuideError,
  setGuideRowsFailed,
  applyGuideRowsFailure,
  setCurrentWindowStartTime,
  setIsPastGuide,
  resetGuideRows,
  resetGuideState,
  resetGuideToLivePosition,
  setGuideHighlightVerticalPosition,
  setGuideHighlightHorizontalPosition,
  setGuideGridFocus,
  setGuideSidebarFocus,
  setIsSidebarFocused,
  closeGuideFilterSidebar,
  setChannelScrollOffset,
  setGuideRowsDirect,
  mergeGuideRows,
  setFutureWindowStartTime,
  setPastWindowStartTime,
  shiftTimeAxisForward,
  shiftTimeAxisBackward,
  advanceWindowForAutoRefresh,
  refreshGuideSessionAnchor,
  applyGuideFilterToggle,
  setHasFavoriteChannels,
  removeGuideFilterSelection,
  applyCategoryFilterChannelIds,
  applyCategoryFilterChannelIdsFailed,
  setGuideChannels,
  setIsFilterSidebarExpanded,
  clearGuidePreview
} = guideSlice.actions;

/**
 * True when a filter is active and leaves nothing in the grid — a category
 * that failed to resolve or matched no channels, or Favorites with nothing
 * favorited (Favorites gets no special treatment). Not reported while the
 * category resolution is still pending (`"idle"` with categories selected).
 */
const isGuideGridEmptyByFilter = (guide: GuideState): boolean => {
  if (!isAnyGuideFilterActive(guide)) {
    return false;
  }
  if (
    getSelectedCategoryFilterIds(guide).length > 0 &&
    guide.categoryFilterResolutionStatus === "idle"
  ) {
    return false;
  }
  return getEffectiveChannelCount(guide) === 0;
};

/** True when the sidebar has focus and an empty filtered grid keeps it there. */
const isSidebarPinnedByEmptyGrid = (guide: GuideState): boolean =>
  guide.isSidebarFocused && isGuideGridEmptyByFilter(guide);

// ─── Navigation Thunk Commands ────────────────────────────────────────────────

/**
 * Repositions the grid's anchor channel after a guide filter toggle (or at
 * mount-time positioning — see `positionGuideHighlightToLastTunedChannel`),
 * per the platform-neutral anchor rules:
 * - Favorites active (even alongside a category selection — Favorites
 *   dominates): playing channel is itself a favorite → anchor there;
 *   otherwise → anchor to the lowest-numbered favorite channel.
 * - Category-only selection (no Favorites): anchor to the first matching
 *   channel (index 0) — there is no "lowest numbered" rule for categories.
 * - No filter active: playing channel known → anchor there; otherwise →
 *   anchor to the first channel (index 0).
 *
 * Always resets horizontalPosition to 1 (first program cell) — mirrors the
 * previous inline reset the pure toggle reducer used to own before
 * positioning moved entirely into this one function for both filter kinds.
 *
 * No-ops (beyond the horizontalPosition reset) when the target channel list
 * is empty (e.g. a category resolved to zero channels) or the anchor channel
 * cannot be located within it — the empty/failure-state UI takes over
 * instead (see `selectShouldShowGuideFilterEmptyState`).
 *
 * @param dispatch Thunk dispatch used to apply the resulting position.
 * @param state Guide state snapshot taken right after the filter reducer ran.
 */
const applyGuideFilterAnchor = async (
  dispatch: (action: UnknownAction) => unknown,
  state: GuideState
): Promise<void> => {
  dispatch(setGuideHighlightHorizontalPosition(1));

  const channelNumber: string | undefined =
    await DeviceModel.getLastTunedChannelNumber();
  const hasPlayingChannel: boolean =
    channelNumber !== undefined && channelNumber.length > 0;

  const anyFilterActive: boolean = isAnyGuideFilterActive(state);
  const targetChannels: TChannel[] = anyFilterActive
    ? state.channels.filter((channel: TChannel): boolean =>
        matchesActiveGuideFilters(state, channel)
      )
    : state.channels;

  if (targetChannels.length === 0) {
    return;
  }

  let anchorChannelNumber: string | undefined;

  if (isFavoritesFilterSelected(state)) {
    const playingIsFavorite: boolean =
      hasPlayingChannel &&
      targetChannels.some(
        (channel: TChannel): boolean =>
          channel.channelNumber === channelNumber && channel.isFavorite === true
      );
    if (playingIsFavorite) {
      anchorChannelNumber = channelNumber;
    } else {
      const lowest: TChannel | null =
        findLowestNumberedFavoriteChannel(targetChannels);
      anchorChannelNumber = lowest !== null ? lowest.channelNumber : undefined;
    }
  } else if (anyFilterActive) {
    anchorChannelNumber = targetChannels[0].channelNumber;
  } else if (hasPlayingChannel) {
    anchorChannelNumber = channelNumber;
  } else {
    anchorChannelNumber = targetChannels[0].channelNumber;
  }

  if (anchorChannelNumber === undefined) {
    return;
  }

  const effectiveVPos: number = Math.min(
    GUIDE_START_CHANNEL_ROW,
    targetChannels.length - 1
  );
  const offset: number | null = computeChannelScrollOffsetForChannel(
    targetChannels,
    anchorChannelNumber,
    effectiveVPos
  );

  if (offset === null) {
    return;
  }

  dispatch(setChannelScrollOffset(offset));
  dispatch(guideSlice.actions.setVerticalPosition(effectiveVPos));
};

/**
 * Re-fetches the master channel list from `ChannelsInteractor` (backed by
 * the channel service's own cache — the same call `loadGuideChannels` makes
 * for the initial load) and narrows it down to the channels matching the
 * currently active guide filter selection, replacing `state.channels`/
 * `state.channelCount` outright via `setGuideChannels`. `state.channels`
 * is never its own persisted "master copy" of the catalog — it always
 * reflects whatever the current selection resolves to, refreshed from the
 * cached channel service on every apply, so the grid and every other
 * consumer of `state.channels` (navigation, scrolling, jump-to-channel) can
 * read it directly instead of re-deriving an "effective" filtered view.
 *
 * A no-op filter selection (nothing selected) still refreshes
 * `state.channels` from the latest cached master list, so a cleared
 * selection restores the full, up-to-date catalog rather than whatever
 * stale list happened to be narrowed in from a previous filter.
 *
 * @param dispatch Thunk dispatch used to apply the resulting state.
 * @param state Guide state snapshot with the settled filter selection
 * (`selectedFilterIds`/`categoryFilterChannelIds`/
 * `categoryFilterResolutionStatus`) already up to date.
 */
const refreshGuideChannelsForActiveFilters = async (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  state: GuideState
): Promise<void> => {
  const interactor: ChannelsInteractor = new ChannelsInteractor();
  let result;
  try {
    result = await interactor.fetchAllChannels();
  } catch (error: unknown) {
    const errorCode: string = channelsErrorCode(error, "");
    const errorType: string = errorTypeName(error);
    Log.e(TAG, "refreshGuideChannelsForActiveFilters()", error);
    DiagnosticLogger.log(
      "guide_lifecycle",
      "refreshGuideChannelsForActiveFilters failed",
      DiagnosticSeverity.Error,
      {
        step: "channels_refresh_failed",
        error_code: errorCode,
        error_type: errorType
      }
    );
    throw error;
  }
  const allChannels: TChannel[] = result.channels;
  dispatch(setHasFavoriteChannels(hasAnyFavoriteChannel(allChannels)));

  if (
    state.categoryFilterResolutionStatus === "resolved" &&
    state.categoryFilterChannelIds.length > 0
  ) {
    // /v2/guideChannelIds is known to occasionally return ids with no
    // corresponding channel at all (distinct from the "gc."/"ch." prefix
    // mismatch normalizeChannelFilterId already works around) — logged for
    // visibility, otherwise ignored: matchesActiveGuideFilters already
    // excludes them from the filtered view without any special handling.
    const unmatchedCategoryIds: string[] = findUnmatchedCategoryChannelIds(
      state.categoryFilterChannelIds,
      allChannels
    );
    if (unmatchedCategoryIds.length > 0) {
      Log.w(
        TAG,
        "refreshGuideChannelsForActiveFilters() - guideChannelIds returned ids with no matching channel: " +
          unmatchedCategoryIds.join(", ")
      );
    }
  }

  const channels: TChannel[] = isAnyGuideFilterActive(state)
    ? allChannels.filter((channel: TChannel): boolean =>
        matchesActiveGuideFilters(state, channel)
      )
    : allChannels;

  dispatch(setGuideChannels({ channels, channelCount: result.channelCount }));
};

/**
 * Runs the guide filter apply flow (§5.2/§5.4 of the guide filtering spec):
 * persists the settled selection, resolves the non-favorite (category)
 * portion against `/v2/guideChannelIds` (skipped entirely when nothing
 * non-favorite is selected), clears the EPG cache (a changed channel set
 * invalidates every previously-cached cell wholesale), repositions the
 * anchor channel, and reloads the window so the repositioned rows actually
 * have program data.
 *
 * Called only once the apply debounce has settled — reads state fresh via
 * `getState()` rather than a closed-over snapshot so it always acts on
 * whatever selection is current *at the moment it runs*, not whatever it
 * was when the debounce was scheduled. Discards itself (before persisting
 * or touching the cache) if a later toggle has since scheduled a newer
 * apply — `version` no longer matches `guideFilterApplyVersion` — so a
 * stale, already-superseded selection can never be the one that's applied.
 *
 * @param dispatch Thunk dispatch used to apply resulting state/side effects.
 * @param getState Thunk getState used to read the settled selection.
 * @param version This job's version, captured when it was scheduled.
 * @param reuseResolvedCategoryIds When true, the already-resolved category
 * channel ids are kept instead of re-calling `/v2/guideChannelIds` (used when
 * only the Favorites selection changed).
 */
const runGuideFilterApply = async (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  getState: () => UnityRootState,
  version: number,
  reuseResolvedCategoryIds: boolean = false
): Promise<void> => {
  if (version !== guideFilterApplyVersion) {
    return;
  }

  const settled: GuideState = (getState() as GuideThunkState).guide;

  GuideCache.getInstance().clear();

  await persistSelectedFilterIds(settled.selectedFilterIds);

  const categoryIds: string[] = getSelectedCategoryFilterIds(settled);
  if (categoryIds.length === 0) {
    dispatch(applyCategoryFilterChannelIds({ status: "idle", channelIds: [] }));
  } else if (
    !(
      reuseResolvedCategoryIds &&
      settled.categoryFilterResolutionStatus === "resolved"
    )
  ) {
    try {
      const interactor: FilteredGuideInteractor = new FilteredGuideInteractor();
      const channelIds: string[] = await interactor.fetchGuideChannelIds({
        menuItemId: new Set(categoryIds)
      });
      if (version !== guideFilterApplyVersion) {
        return;
      }
      dispatch(
        applyCategoryFilterChannelIds({ status: "resolved", channelIds })
      );
    } catch (error: unknown) {
      Log.e(TAG, "runGuideFilterApply() - fetchGuideChannelIds failed", error);
      if (!isCancellation(error)) {
        OperationalLogger.logUiError({
          errorCode: GuideErrorMapper.toCompositeCode(
            GUIDE_ERROR_CODE.FILTER_CHANNEL_IDS_FETCH_FAILED,
            error
          ),
          errorMessage: GuideErrorMapper.toErrorMessage(
            error,
            FILTER_CHANNEL_IDS_FAILURE_MESSAGE
          ),
          screen: GUIDE_SCREEN_NAME
        });
      }
      if (version !== guideFilterApplyVersion) {
        return;
      }
      dispatch(applyCategoryFilterChannelIdsFailed());
    }
  }

  if (version !== guideFilterApplyVersion) {
    return;
  }

  const resolvedState: GuideState = (getState() as GuideThunkState).guide;
  await refreshGuideChannelsForActiveFilters(dispatch, resolvedState);

  if (version !== guideFilterApplyVersion) {
    return;
  }

  const stateWithChannels: GuideState = (getState() as GuideThunkState).guide;
  await applyGuideFilterAnchor(dispatch, stateWithChannels);
  if (isGuideGridEmptyByFilter(stateWithChannels)) {
    if (!stateWithChannels.isSidebarFocused) {
      dispatch(setIsSidebarFocused(true));
    }
    return;
  }
  await dispatch(loadGuideWindow());
};

/**
 * Schedules `runGuideFilterApply` after `GUIDE_FILTER_APPLY_DEBOUNCE_MS` of
 * no further toggles, cancelling any apply already in flight. Multi-select
 * in the expanded overlay means several toggles can land in quick
 * succession; without this, each one would independently trigger a network
 * round-trip plus a full EPG cache wipe and reload — wasteful, and able to
 * show intermediate, immediately-superseded filter states. Returns a
 * promise that settles once the (possibly no-op, if superseded) settle
 * attempt completes — rejecting if `runGuideFilterApply` throws, so
 * `toggleGuideFilter`'s own try/catch still observes an anchor/reload
 * failure the same way it did before this was debounced.
 *
 * @param dispatch Thunk dispatch used to apply resulting state/side effects.
 * @param getState Thunk getState used to read the settled selection.
 */
const scheduleGuideFilterApply = (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  getState: () => UnityRootState,
  reuseResolvedCategoryIds: boolean = false
): Promise<void> => {
  if (guideFilterApplyTimeoutId !== null) {
    clearTimeout(guideFilterApplyTimeoutId);
  }
  const version: number = ++guideFilterApplyVersion;

  return new Promise<void>((resolve, reject) => {
    guideFilterApplyTimeoutId = setTimeout((): void => {
      guideFilterApplyTimeoutId = null;
      runGuideFilterApply(
        dispatch,
        getState,
        version,
        reuseResolvedCategoryIds
      ).then(resolve, reject);
    }, GUIDE_FILTER_APPLY_DEBOUNCE_MS);
  });
};

/**
 * View-layer entry point for toggling a single guide filter (Favorites or a
 * category) — the one thunk both filter kinds share, generalizing the former
 * (now-removed) `toggleFavoritesFilter`. Applies the pure
 * `applyGuideFilterToggle` reducer immediately (so the checkmark/selection
 * update feels instant), then debounces the actual apply — persisting,
 * category-id resolution, EPG cache invalidation, anchor-channel
 * repositioning, and a window reload — via `scheduleGuideFilterApply` so a
 * burst of rapid multi-select toggles settles exactly once.
 *
 * @param filterId The id of the filter item being toggled.
 */
export const toggleGuideFilter = createAsyncThunk<
  void,
  string,
  { state: UnityRootState; rejectValue: string }
>(
  "guide/toggleGuideFilter",
  async (filterId: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const before: GuideState = (getState() as GuideThunkState).guide;
      const matchingFilter: TGuideFilterItem | undefined =
        before.filterItems.find(
          (item: TGuideFilterItem): boolean => item.id === filterId
        );
      if (matchingFilter === undefined) {
        return;
      }
      if (matchingFilter.isFavorite && !before.hasFavoriteChannels) {
        return;
      }

      dispatch(applyGuideFilterToggle({ filterId }));

      const after: GuideState = (getState() as GuideThunkState).guide;

      DiagnosticLogger.log(
        "guide_filter",
        "guide filter toggled: " + filterId,
        DiagnosticSeverity.Info,
        {
          filter_id: filterId,
          is_active: String(after.selectedFilterIds.includes(filterId))
        }
      );
      GuideAnalyticsLogger.logItemSelected({
        list_name: "FilterSidebar",
        tile_position: String(
          after.filterItems.findIndex(
            (item: TGuideFilterItem): boolean => item.id === filterId
          )
        ),
        tile_id: filterId,
        is_favorite: String(matchingFilter.isFavorite)
      });

      // Toggling Favorites leaves the selected category ids untouched, so the
      // already-resolved ids are reused instead of re-calling
      // /v2/guideChannelIds.
      await scheduleGuideFilterApply(
        dispatch,
        getState,
        matchingFilter.isFavorite
      );
    } catch (error: unknown) {
      Log.e(TAG, "toggleGuideFilter()", error);
      const message: string =
        error instanceof Error
          ? error.message
          : "Failed to toggle guide filter";
      return rejectWithValue(message);
    }
  }
);

/**
 * True only on a fresh Guide entry from Home. Gates every mount-time reset
 * that should not clobber a sticky position (highlight row/channel, and
 * past/future time browsing) when the user instead returned to Guide from
 * Play or any other screen.
 */
const selectIsGuideEntryFromHome = (state: UnityRootState): boolean =>
  selectPreviousUrl(state) === LocationUrls.HOME_SCREEN;

/**
 * Snaps Guide's current/past/future window start times and isPastGuide/
 * horizontalPosition back to the live (present) position, on a fresh entry
 * from Home. Guide reached from anywhere else (e.g. returning from Play)
 * leaves all of it untouched — including the window start times — so a
 * deep past/future browsing position (not just which of the three windows
 * was focused) sticks exactly where it was left, matching
 * {@link positionGuideHighlightToLastTunedChannel}.
 *
 * The window start times are also seeded whenever they are still null,
 * regardless of entry point. null only occurs on a true first load —
 * notably a hard page reload landing directly on Guide, where there is no
 * "previous" screen for {@link selectIsGuideEntryFromHome} to match, so
 * without this the window times would stay null forever and
 * {@link loadGuideWindow} would silently no-op on every target.
 */
export const resetGuideToLivePositionIfFromHome = createAsyncThunk<
  void,
  void,
  { state: UnityRootState }
>(
  "guide/resetGuideToLivePositionIfFromHome",
  (_arg, { dispatch, getState }) => {
    const state: GuideState = (getState() as GuideThunkState).guide;
    const isFromHome: boolean = selectIsGuideEntryFromHome(getState());
    const needsInitialSeed: boolean = state.currentWindowStartTime === null;

    if (!isFromHome && !needsInitialSeed) {
      return;
    }

    const now: DateTime = DateTime.roundTimeToNearestInterval(
      new DateTime(),
      GUIDE_TIME_SLOT_INTERVAL_MINUTES,
      true
    );
    dispatch(setCurrentWindowStartTime(now));
    dispatch(
      setPastWindowStartTime(now.subtractMinutes(GUIDE_WINDOW_DURATION_MINUTES))
    );
    dispatch(
      setFutureWindowStartTime(now.addMinutes(GUIDE_WINDOW_DURATION_MINUTES))
    );

    if (isFromHome) {
      dispatch(resetGuideToLivePosition());
    }
  }
);

/**
 * Positions the guide highlight on the last tuned channel.
 *
 * Only applies on a fresh entry from Home (§ highlight stickiness) — Guide
 * reached from anywhere else (e.g. returning from Play, or any other
 * screen) leaves channelScrollOffset/verticalPosition untouched so the
 * highlight stays where the user left it.
 *
 * Otherwise reads the persisted last tuned channel number, locates it in
 * the loaded channel list, and sets channelScrollOffset so the channel
 * lands under the highlight row. No-ops when there is no last tuned channel
 * or it is not present in the current channel list.
 *
 * When any guide filter is active, delegates to
 * {@link applyGuideFilterAnchor} so the anchor rules are applied
 * consistently.
 */
export const positionGuideHighlightToLastTunedChannel = createAsyncThunk<
  void,
  void,
  { state: UnityRootState }
>(
  "guide/positionHighlightToLastTunedChannel",
  async (_arg, { dispatch, getState }) => {
    if (!selectIsGuideEntryFromHome(getState())) {
      return;
    }

    const state: GuideState = (getState() as GuideThunkState).guide;

    if (isAnyGuideFilterActive(state)) {
      await applyGuideFilterAnchor(dispatch, state);
      if (isGuideGridEmptyByFilter(state) && !state.isSidebarFocused) {
        dispatch(setIsSidebarFocused(true));
      }
      return;
    }

    const channelNumber: string | undefined =
      await DeviceModel.getLastTunedChannelNumber();

    if (channelNumber === undefined || channelNumber.length === 0) {
      return;
    }

    const effectiveVerticalPosition: number = Math.min(
      state.verticalPosition,
      GUIDE_HIGHLIGHT_ROW_MAX
    );
    const offset: number | null = computeChannelScrollOffsetForChannel(
      state.channels,
      channelNumber,
      effectiveVerticalPosition
    );

    if (offset === null) {
      return;
    }

    dispatch(setChannelScrollOffset(offset));
  }
);

interface GuideWindowLoadTarget {
  time: DateTime | null;
  timeWindow: GuideTimeWindow;
}

/**
 * Handles a guide-rows service failure encountered while the user is
 * actively browsing the grid (cache already populated, possibly scrolled
 * into a past/future window).
 *
 * Clears the in-memory guide cache, invalidates any in-flight window load so
 * stale results can't repopulate the grid, then snaps the visible window
 * back to "now" so only the fallback cells are shown and the user is never
 * left stranded in a past/future window with no data.
 */
export const handleGuideRowsFailure = createAsyncThunk<void, void>(
  "guide/handleGuideRowsFailure",
  async (_arg, { dispatch }) => {
    try {
      DiagnosticLogger.log(
        "guide_lifecycle",
        "guide error recovery: snapping_to_now",
        DiagnosticSeverity.Warning,
        { event: "snapping_to_now" }
      );
      const cache: GuideCache = GuideCache.getInstance();
      cache.abortAndReset();
      cache.clear();

      // [G6] Bump the epoch so any in-flight loadGuideWindow result
      // (e.g. a past/future prefetch already underway) is discarded rather
      // than overwriting the cleared, snapped-to-now state below.
      dispatch(_incrementWindowLoadGeneration());

      const now: DateTime = DateTime.roundTimeToNearestInterval(
        new DateTime(),
        GUIDE_TIME_SLOT_INTERVAL_MINUTES,
        true
      );
      dispatch(applyGuideRowsFailure(now));
    } catch (error: unknown) {
      Log.e(TAG, "handleGuideRowsFailure()", error);
    }
  }
);

/** Number of extra attempts to reload the visible window before giving up. */
const GUIDE_VISIBLE_RETRY_MAX: number = 2;

/** Base delay between visible-window retries (grows linearly per attempt). */
const GUIDE_VISIBLE_RETRY_DELAY_MS: number = 400;

/** Resolves after the given delay. */
const delayMs = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Bounded recovery for the on-screen window: retries the visible fetch a few
 * times with linear backoff before the caller snaps back to "now". Navigation
 * is never blocked — this only governs when data appears. Bails out early (as
 * success) if a newer navigation supersedes this load.
 *
 * @returns True when recovered or superseded; false when all retries failed.
 */
const retryVisibleWindow = async (
  cache: GuideCache,
  time: DateTime,
  channels: TChannel[],
  startGlobalIndex: number,
  epoch: number,
  dispatch: (action: UnknownAction) => unknown,
  getState: () => unknown
): Promise<boolean> => {
  for (let attempt: number = 1; attempt <= GUIDE_VISIBLE_RETRY_MAX; attempt++) {
    if ((getState() as GuideThunkState).guide.windowLoadGeneration !== epoch) {
      return true;
    }
    await delayMs(GUIDE_VISIBLE_RETRY_DELAY_MS * attempt);
    if ((getState() as GuideThunkState).guide.windowLoadGeneration !== epoch) {
      return true;
    }
    try {
      const rows: TGridGuideRow[] = await cache.fetchSmart(time, channels);
      if (
        (getState() as GuideThunkState).guide.windowLoadGeneration !== epoch
      ) {
        return true;
      }
      dispatch(
        mergeGuideRows({
          rows,
          timeWindow: GuideTimeWindow.CURRENT,
          startGlobalIndex
        })
      );
      dispatch(setGuideRowsFailed(false));
      return true;
    } catch (error: unknown) {
      // Cancelled means a newer load already owns the window — stop retrying
      // and report success so the caller does not fall through to the
      // snap-back recovery for a request that was simply superseded.
      if (isCancellation(error)) {
        Log.d(TAG, "retryVisibleWindow — cancelled (superseded)");
        return true;
      }
      Log.e(TAG, "retryVisibleWindow — retry " + attempt + " failed", error);
    }
  }
  return false;
};

/**
 * Loads guide rows for the current sliding window of channels across all three
 * time buckets (past, current, future).
 *
 * Updates the in-memory channel window, prunes program data for channels that
 * scrolled out of view, then fetches (cache-first) and merges rows for the
 * windowed channels at their absolute indices. Only GUIDE_TOTAL_CACHED_CHANNELS
 * channels' program data is held in memory at any time.
 */
export const loadGuideWindow = createAsyncThunk<void, void>(
  "guide/loadGuideWindow",
  async (_arg, { dispatch, getState }) => {
    const state: GuideState = (getState() as GuideThunkState).guide;
    const window: GuideWindowChannels = getGuideWindowChannels(state);

    if (window.channels.length === 0) {
      return;
    }

    // [G6] Snapshot the generation at entry. Any subsequent page-boundary
    // scroll will increment it; we check again after each await and abort
    // if superseded — preventing stale results from overwriting newer data.
    const generation: number = state.windowLoadGeneration;

    const cache: GuideCache = GuideCache.getInstance();

    // [G7] Cancel any fetchChunk network requests from the previous
    // loadGuideWindow invocation. fetchChunk guards its mergeRows call
    // with a post-await abort-signal check, so rows that arrive after
    // the abort are silently discarded rather than polluting the cache.
    cache.abortAndReset();

    cache.setChannelWindow(
      window.channels,
      window.startGlobalIndex,
      state.channels.length
    );
    cache.pruneProgramsToWindow();

    const currentTarget: GuideWindowLoadTarget = {
      time: state.currentWindowStartTime,
      timeWindow: GuideTimeWindow.CURRENT
    };
    const adjacentTargets: GuideWindowLoadTarget[] = [
      { time: state.pastWindowStartTime, timeWindow: GuideTimeWindow.PAST },
      { time: state.futureWindowStartTime, timeWindow: GuideTimeWindow.FUTURE }
    ];

    // [G5] Immediately serve whatever is in cache for every window (may have
    // empty guideCells for stations not yet populated). This gives the UI an
    // instant paint — including the edge cells fed by past/future — before the
    // network fills any gaps.
    for (const target of [currentTarget, ...adjacentTargets]) {
      if (target.time === null) {
        continue;
      }
      const partialRows: TGridGuideRow[] = cache.getWindowRows(
        target.time,
        window.channels
      );
      dispatch(
        mergeGuideRows({
          rows: partialRows,
          timeWindow: target.timeWindow,
          startGlobalIndex: window.startGlobalIndex
        })
      );
    }

    const loadTarget = async (target: GuideWindowLoadTarget): Promise<void> => {
      if (target.time === null) {
        return;
      }

      // [G3] fetchSmart fetches only uncovered stations (GAP_FILL) or skips
      // the network entirely when every station is already cached (CACHE_HIT).
      try {
        const fullRows: TGridGuideRow[] = await cache.fetchSmart(
          target.time,
          window.channels
        );

        // [G6] Discard result if a newer scroll has already superseded us.
        if (
          (getState() as GuideThunkState).guide.windowLoadGeneration !==
          generation
        ) {
          Log.d(
            TAG,
            "loadGuideWindow — discarding stale result (generation mismatch)"
          );
          return;
        }

        dispatch(
          mergeGuideRows({
            rows: fullRows,
            timeWindow: target.timeWindow,
            startGlobalIndex: window.startGlobalIndex
          })
        );

        // The visible (current) window loaded successfully — clear any
        // previously-recorded guide-rows service failure.
        if (target.timeWindow === GuideTimeWindow.CURRENT) {
          dispatch(setGuideRowsFailed(false));
        }
      } catch (error: unknown) {
        // A superseded request is not a failure. Recovering from it would run
        // the bounded retry and, if that is also cancelled, snap the window
        // back to "now" — yanking the user home mid-navigation.
        if (isCancellation(error)) {
          Log.d(TAG, "loadGuideWindow — request cancelled (superseded)");
          return;
        }

        Log.e(TAG, "loadGuideWindow — fetchSmart failed", error);

        // Only the on-screen (current) window failure is surfaced to the UI.
        // Past/future prefetch failures remain best-effort and never affect
        // what is currently rendered.
        if (target.timeWindow === GuideTimeWindow.CURRENT) {
          // Bounded recovery: retry the visible window before snapping back
          // to "now". Navigation stays live throughout.
          dispatch(setGuideNavMode(GuideNavMode.RECOVERING));
          DiagnosticLogger.log(
            "guide_lifecycle",
            "guide error recovery: visible_window_recovering",
            DiagnosticSeverity.Warning,
            { event: "visible_window_recovering" }
          );
          const recovered: boolean = await retryVisibleWindow(
            cache,
            target.time,
            window.channels,
            window.startGlobalIndex,
            generation,
            dispatch,
            getState
          );
          if (!recovered) {
            dispatch(setGuideNavMode(GuideNavMode.ERROR));
            await dispatch(handleGuideRowsFailure());
          }
        }
      }
    };

    // Load the visible window first and let it settle before warming the
    // adjacent ones. Firing all three concurrently left the on-screen window
    // competing with off-screen prefetch for connections, so it could resolve
    // last and flip to TBA while past/future were already cached.
    await loadTarget(currentTarget);
    await Promise.all(adjacentTargets.map(loadTarget));

    // [G6] Final stale-check before the time-buffer prefetch (best-effort).
    if (
      (getState() as GuideThunkState).guide.windowLoadGeneration !== generation
    ) {
      return;
    }

    // Warm the time buffer on each side of the visible window so subsequent
    // left/right shifts are served from memory. Best-effort: failures here must
    // never break the visible load.
    if (state.currentWindowStartTime !== null) {
      try {
        await cache.prefetchTimeBuffer(
          state.currentWindowStartTime,
          window.channels
        );
      } catch (error: unknown) {
        Log.e(TAG, "loadGuideWindow — time buffer prefetch failed", error);
      }
    }

    // Reflect the settled (or error) mode when this load is still the latest.
    if (
      (getState() as GuideThunkState).guide.windowLoadGeneration === generation
    ) {
      dispatch(
        setGuideNavMode(
          (getState() as GuideThunkState).guide.isGuideRowsFailed
            ? GuideNavMode.ERROR
            : GuideNavMode.IDLE
        )
      );
    }
  }
);

/**
 * Settles the guide after a turbo scroll (or any deferred move): stamps a fresh
 * navigation epoch, then loads the final visible window and its surrounding
 * cache windows via loadGuideWindow. Because the epoch is bumped first, an
 * earlier settle still in flight is discarded — the latest key release wins.
 * loadGuideWindow finalizes navMode to IDLE (or ERROR when unrecoverable).
 */
export const settleGuideWindow = createAsyncThunk<void, void>(
  "guide/settleGuideWindow",
  async (_arg, { dispatch }) => {
    // A settle is about to run — any pending debounced settle would be
    // redundant (loadGuideWindow already reconciles from the cache).
    if (guideSettleTimeoutId !== null) {
      clearTimeout(guideSettleTimeoutId);
      guideSettleTimeoutId = null;
    }
    dispatch(_incrementWindowLoadGeneration());
    dispatch(setGuideNavMode(GuideNavMode.SETTLING));
    await dispatch(loadGuideWindow());
  }
);

/**
 * Clears the pending debounced settle timer, if any.
 * Exported for component unmount cleanup.
 */
export const clearGuideSettleTimer = (): void => {
  if (guideSettleTimeoutId !== null) {
    clearTimeout(guideSettleTimeoutId);
    guideSettleTimeoutId = null;
  }
};

/**
 * (Re)starts the quiet-period timer that settles the guide once directional
 * navigation stops. Called from every navigateGuide{Up,Down,Left,Right}
 * invocation, turbo or not, so a burst of fast *discrete* presses — which
 * never engage turbo, since native key-repeat never fires for a tap — still
 * settles shortly after the burst ends. Without this, a later press's time/
 * channel-window shift can supersede an earlier press's in-flight fetch (see
 * the generation-mismatch guards above); that fetch's rows are real and
 * already cached in GuideCache, but were never re-requested, so the row stays
 * on a loading placeholder indefinitely.
 *
 * Deliberately module-level rather than React/ScreenController state: it must
 * survive independently of component re-renders and ScreenController
 * instance churn (see guideSettleTimeoutId's doc comment).
 *
 * @param dispatch Store dispatch.
 * @returns Nothing.
 */
const scheduleGuideSettle = (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
): void => {
  if (guideSettleTimeoutId !== null) {
    clearTimeout(guideSettleTimeoutId);
  }
  guideSettleTimeoutId = setTimeout((): void => {
    guideSettleTimeoutId = null;
    Log.d(TAG, "navigation quiesced — settling guide window");
    dispatch(settleGuideWindow());
  }, GUIDE_NAV_SETTLE_DEBOUNCE_MS);
};

/**
 * Fires when the wall-clock time crosses a 30-minute boundary while the guide
 * is active. Advances the visible window to the new boundary and reloads guide
 * data via the existing cache-first loadGuideWindow mechanism.
 *
 * Three guards prevent undesired window snaps:
 *   1. OSD visible — any overlay (antiburn, error, etc.) is showing; skip.
 *   2. isPastGuide — user is browsing replay data; skip.
 *   3. Window position mismatch — user has scrolled away from the On Now
 *      window; skip to avoid jumping the grid back to "now" unexpectedly.
 *
 * Guards 2 and 3 only suppress moving the visible window — they still refresh
 * guideSessionStartMs to the current boundary via refreshGuideSessionAnchor.
 * Without that, a guide session left idle for hours while parked in the past
 * or future would measure its next forward/backward shift against a stale
 * anchor instead of the real elapsed time back to live.
 */
export const autoRefreshGuideWindow = createAsyncThunk<void, void>(
  "guide/autoRefreshGuideWindow",
  async (_arg, { dispatch, getState }) => {
    try {
      const state: GuideState = (getState() as GuideThunkState).guide;
      const intervalMs: number = Math.max(GUIDE_AUTO_REFRESH_INTERVAL_MS, 1);
      const nowMs: number = Date.now();
      const newBoundaryMs: number = nowMs - (nowMs % intervalMs);
      const previousBoundaryMs: number = newBoundaryMs - intervalMs;

      // Guard 1: skip while any OSD overlay is on screen
      if ((getState() as UnityRootState).osd.isVisible) {
        Log.d(TAG, "autoRefreshGuideWindow() — skipping, OSD is visible");
        DiagnosticLogger.log(
          "guide_lifecycle",
          "auto-refresh guard: osd_visible",
          DiagnosticSeverity.Debug,
          { guard: "osd_visible", skipped: "true" }
        );
        return;
      }

      // Guard 2: only refresh in live/forward mode
      if (state.isPastGuide) {
        Log.d(TAG, "autoRefreshGuideWindow() — skipping, isPastGuide is true");
        DiagnosticLogger.log(
          "guide_lifecycle",
          "auto-refresh guard: is_past_guide",
          DiagnosticSeverity.Debug,
          { guard: "is_past_guide", skipped: "true" }
        );
        dispatch(refreshGuideSessionAnchor(newBoundaryMs));
        return;
      }

      // Guard 3: only refresh if the user is viewing the On Now window
      if (
        state.currentWindowStartTime === null ||
        state.currentWindowStartTime.getTime() !== previousBoundaryMs
      ) {
        Log.d(
          TAG,
          "autoRefreshGuideWindow() — skipping, user has navigated away from On Now"
        );
        DiagnosticLogger.log(
          "guide_lifecycle",
          "auto-refresh guard: window_moved_from_now",
          DiagnosticSeverity.Debug,
          { guard: "window_moved_from_now", skipped: "true" }
        );
        dispatch(refreshGuideSessionAnchor(newBoundaryMs));
        return;
      }

      Log.d(
        TAG,
        "autoRefreshGuideWindow() — advancing to boundary " + newBoundaryMs
      );
      DiagnosticLogger.log(
        "guide_lifecycle",
        "auto-refresh guard: window_at_now",
        DiagnosticSeverity.Debug,
        { guard: "window_at_now", skipped: "false" }
      );

      dispatch(advanceWindowForAutoRefresh(newBoundaryMs));
      await dispatch(loadGuideWindow());
    } catch (error: unknown) {
      Log.e(TAG, "autoRefreshGuideWindow()", error);
    }
  }
);

/**
 * Re-resolves the time-based channel narrowing (category ids from
 * `/v2/guideChannelIds` plus the cached master channel list) for the active
 * filter selection. Best-effort: on any failure the previous channel list and
 * resolved ids are left untouched and nothing is surfaced to the user.
 *
 * @returns True when the visible channel set changed.
 */
const refreshActiveFilterChannelsBestEffort = async (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  getState: () => UnityRootState
): Promise<boolean> => {
  const guideStateOf = (): GuideState => (getState() as GuideThunkState).guide;
  const before: TChannel[] = guideStateOf().channels;
  const highlightedBefore: TChannel | undefined =
    before[
      guideStateOf().channelScrollOffset + guideStateOf().verticalPosition
    ];
  const version: number = guideFilterApplyVersion;

  try {
    const categoryIds: string[] = getSelectedCategoryFilterIds(guideStateOf());
    if (categoryIds.length > 0) {
      let channelIds: string[];
      try {
        channelIds = await new FilteredGuideInteractor().fetchGuideChannelIds({
          menuItemId: new Set(categoryIds)
        });
      } catch (fetchError: unknown) {
        if (!isCancellation(fetchError)) {
          OperationalLogger.logUiError({
            errorCode: GuideErrorMapper.toCompositeCode(
              GUIDE_ERROR_CODE.FILTER_CHANNEL_IDS_FETCH_FAILED,
              fetchError
            ),
            errorMessage: GuideErrorMapper.toErrorMessage(
              fetchError,
              FILTER_CHANNEL_IDS_FAILURE_MESSAGE
            ),
            screen: GUIDE_SCREEN_NAME
          });
        }
        throw fetchError;
      }
      if (version !== guideFilterApplyVersion) {
        return false;
      }
      dispatch(
        applyCategoryFilterChannelIds({ status: "resolved", channelIds })
      );
    }
    await refreshGuideChannelsForActiveFilters(dispatch, guideStateOf());
  } catch (error: unknown) {
    Log.w(
      TAG,
      "refreshActiveFilterChannelsBestEffort() - keeping previous list"
    );
    Log.e(TAG, "refreshActiveFilterChannelsBestEffort()", error);
    return false;
  }

  const after: TChannel[] = guideStateOf().channels;
  const idOf = (channel: TChannel): string | undefined => channel.channelId;
  const changed: boolean =
    before.length !== after.length ||
    before.some((channel: TChannel, i: number): boolean => {
      return idOf(channel) !== idOf(after[i]);
    });
  if (!changed) {
    return false;
  }

  // Keep the highlight on the same channel when it survived the refresh,
  // otherwise fall back to the filter-apply anchor rules.
  const resolved: GuideState = guideStateOf();
  const highlightedId: string | undefined = highlightedBefore
    ? highlightedBefore.channelId
    : undefined;
  const survivedIndex: number =
    highlightedId !== undefined
      ? after.findIndex(
          (channel: TChannel): boolean => channel.channelId === highlightedId
        )
      : -1;
  let offset: number | null = null;
  if (survivedIndex !== -1) {
    const row: number = Math.min(resolved.verticalPosition, after.length - 1);
    offset =
      (((survivedIndex - row) % after.length) + after.length) % after.length;
  }
  if (offset !== null) {
    dispatch(setChannelScrollOffset(offset));
  } else {
    await applyGuideFilterAnchor(dispatch, resolved);
  }
  return true;
};

/**
 * Resume refresh used when the antiburn OSD is dismissed or the app returns to
 * the foreground. The narrowed channel list is time based, so it is re-resolved
 * here (best-effort, previous list kept on failure) before the window refresh.
 * In the past guide only the session anchor is refreshed.
 */
export const refreshGuideOnResume = createAsyncThunk<void, void>(
  "guide/refreshGuideOnResume",
  async (_arg, { dispatch, getState }) => {
    try {
      // Past guide: leave channels and window alone, but still refresh the
      // session anchor (autoRefreshGuideWindow does only that in this mode).
      if ((getState() as GuideThunkState).guide.isPastGuide) {
        Log.d(TAG, "refreshGuideOnResume() — past guide, anchor refresh only");
        await dispatch(autoRefreshGuideWindow());
        return;
      }

      const changed: boolean = await refreshActiveFilterChannelsBestEffort(
        dispatch as ThunkDispatch<unknown, unknown, UnknownAction>,
        getState as () => UnityRootState
      );

      if (changed) {
        GuideCache.getInstance().clear();
        const guide: GuideState = (getState() as GuideThunkState).guide;
        if (isGuideGridEmptyByFilter(guide)) {
          if (!guide.isSidebarFocused) {
            dispatch(setIsSidebarFocused(true));
          }
          return;
        }
        await dispatch(loadGuideWindow());
      }

      await dispatch(autoRefreshGuideWindow());
    } catch (error: unknown) {
      Log.e(TAG, "refreshGuideOnResume()", error);
    }
  }
);

/**
 * Prefetches guide data for channels scrolled toward the edge of the cached
 * window after a vertical move, across the full cached time extent. Best-effort:
 * failures are logged and swallowed so navigation is never blocked.
 *
 * @param getState Thunk getState function.
 * @param direction Vertical scroll direction.
 * @returns Nothing.
 */
const prefetchChannelDataForDirection = async (
  getState: () => unknown,
  direction: "up" | "down"
): Promise<void> => {
  const state: GuideState = (getState() as GuideThunkState).guide;
  if (state.currentWindowStartTime === null) {
    return;
  }
  const plan: GuideChannelPrefetchPlan | null = getChannelPrefetchPlan(
    state,
    direction
  );
  if (plan === null || plan.channels.length === 0) {
    return;
  }
  const stationIds: string[] = plan.channels.map((ch: TChannel): string => {
    if (ch.stationId !== undefined && ch.stationId !== null) {
      return ch.stationId;
    }
    return ch.channelId !== undefined && ch.channelId !== null
      ? ch.channelId
      : "";
  });
  const cache: GuideCache = GuideCache.getInstance();
  const remainingChannelBuffer: number =
    cache.getRemainingChannelBuffer(stationIds);
  if (remainingChannelBuffer > GUIDE_PREFETCH_CH_THRESHOLD) {
    return;
  }
  try {
    await cache.prefetchChannelData(
      state.currentWindowStartTime,
      plan.channels
    );
  } catch (error: unknown) {
    Log.e(
      TAG,
      "navigateGuide" + direction + " — channel prefetch failed",
      error
    );
  }
};

/**
 * Moves guide focus up one row. When the move crosses a page boundary
 * (channelScrollOffset moves into the previous batch of GUIDE_BATCH_SIZE
 * channels), reloads the window so newly visible channel rows are fetched
 * (cache-first) and merged into Redux. [DSN-GUIDEBATCH-POL-002]
 */
export const navigateGuideUp = createAsyncThunk<void, void>(
  "guide/navigateGuideUp",
  async (_arg, { dispatch, getState }) => {
    scheduleGuideSettle(dispatch);
    const isTurbo: boolean =
      (getState() as GuideThunkState).guide.navMode === GuideNavMode.TURBO;
    const preOffset: number = (getState() as GuideThunkState).guide
      .channelScrollOffset;

    dispatch(_navigateGuideUpStep());

    const postOffset: number = (getState() as GuideThunkState).guide
      .channelScrollOffset;

    // Turbo: move the window only. Data for the final window loads on settle
    // (key release), so intermediate windows never trigger a fetch.
    if (isTurbo) {
      return;
    }

    if (preOffset !== postOffset) {
      // Page-boundary load: only refetch when first row of next page enters
      // the viewport, i.e. when the scroll crosses a GUIDE_BATCH_SIZE boundary.
      if (
        Math.floor(preOffset / GUIDE_BATCH_SIZE) !==
        Math.floor(postOffset / GUIDE_BATCH_SIZE)
      ) {
        // [G6] Bump the epoch so any in-flight loadGuideWindow from a
        // previous scroll knows its result is now stale.
        dispatch(_incrementWindowLoadGeneration());
        await dispatch(loadGuideWindow());
      }
      await prefetchChannelDataForDirection(getState, "up");
    }
  }
);

/**
 * Moves guide focus down one row. When the move crosses a page boundary
 * (channelScrollOffset moves into the next batch of GUIDE_BATCH_SIZE
 * channels), reloads the window so newly visible channel rows are fetched
 * (cache-first) and merged into Redux. [DSN-GUIDEBATCH-POL-002]
 */
export const navigateGuideDown = createAsyncThunk<void, void>(
  "guide/navigateGuideDown",
  async (_arg, { dispatch, getState }) => {
    scheduleGuideSettle(dispatch);
    const isTurbo: boolean =
      (getState() as GuideThunkState).guide.navMode === GuideNavMode.TURBO;
    const preOffset: number = (getState() as GuideThunkState).guide
      .channelScrollOffset;

    dispatch(_navigateGuideDownStep());

    const postOffset: number = (getState() as GuideThunkState).guide
      .channelScrollOffset;

    // Turbo: move the window only. Data for the final window loads on settle
    // (key release), so intermediate windows never trigger a fetch.
    if (isTurbo) {
      return;
    }

    if (preOffset !== postOffset) {
      // Page-boundary load: only refetch when first row of next page enters
      // the viewport, i.e. when the scroll crosses a GUIDE_BATCH_SIZE boundary.
      if (
        Math.floor(preOffset / GUIDE_BATCH_SIZE) !==
        Math.floor(postOffset / GUIDE_BATCH_SIZE)
      ) {
        // [G6] Bump the epoch so any in-flight loadGuideWindow from a
        // previous scroll knows its result is now stale.
        dispatch(_incrementWindowLoadGeneration());
        await dispatch(loadGuideWindow());
      }
      await prefetchChannelDataForDirection(getState, "down");
    }
  }
);

const NAVIGATE_LEFT_PREFETCH_ERROR: string =
  "navigateGuideLeft — pre-fetch failed";

/**
 * Handles LEFT stalling at the channel cell (hPos 0).
 *
 * Current guide: first LEFT highlights the sidebar icon; second LEFT while the
 * sidebar is highlighted enters the past guide and pre-fetches the past window.
 *
 * Past guide: the channel cell sits on the right edge — LEFT returns focus to
 * the last program cell instead of shifting time backward.
 *
 * @param dispatch Thunk dispatch function.
 * @param getState Thunk getState function.
 * @returns Nothing.
 */
const handleLeftChannelCellStall = async (
  dispatch: (action: UnknownAction) => unknown,
  getState: () => unknown
): Promise<void> => {
  const stallState: GuideState = (getState() as GuideThunkState).guide;

  if (stallState.isPastGuide) {
    const effectiveVPos: number = getEffectiveVerticalPosition(stallState);
    const maxHPos: number = getMaxHorizontalPosition(stallState, effectiveVPos);
    if (maxHPos > 0) {
      dispatch(setGuideHighlightHorizontalPosition(maxHPos));
    }
    return;
  }

  // With no selectable filter (e.g. the filter list failed and only the
  // disabled Favorites fallback is shown) the sidebar is skipped and LEFT goes
  // straight into the past guide.
  const isSidebarSkipped: boolean =
    !stallState.isSidebarFocused && !hasSelectableFilterItem(stallState);

  if (!stallState.isSidebarFocused && !isSidebarSkipped) {
    // First LEFT at channel cell: highlight the sidebar icon.
    dispatch(setIsSidebarFocused(true));
    return;
  }

  // Second LEFT while sidebar is highlighted (or the first, when the sidebar
  // is skipped): enter past guide (blocked when the filter left the grid
  // empty).
  if (isGuideGridEmptyByFilter(stallState)) {
    return;
  }
  if (!isSidebarSkipped) {
    dispatch(closeGuideFilterSidebar());
  }

  // Guide-rows service failure: no program schedule exists for any window,
  // so past guide (beyond the current time window) is unreachable. The
  // sidebar still toggles above; only the time shift into past guide is
  // blocked here.
  if (stallState.isGuideRowsFailed) {
    return;
  }

  dispatch(
    shiftTimeAxisBackward(
      (getState() as UnityRootState).branding.epgLookBackHours
    )
  );
  if (stallState.navMode !== GuideNavMode.TURBO) {
    const epoch: number = (getState() as GuideThunkState).guide
      .windowLoadGeneration;
    await prefetchAndHydrateRows(
      GuideTimeWindow.PAST,
      getState,
      epoch,
      (payload) => {
        dispatch(mergeGuideRows(payload));
      },
      NAVIGATE_LEFT_PREFETCH_ERROR
    );
  }
};

/**
 * Prefetches guide rows for a direction and hydrates the matching window bucket.
 *
 * @param direction Side to prefetch.
 * @param getState Thunk getState function.
 * @param epoch windowLoadGeneration captured after the shift; the merge is skipped when a
 *   newer navigation has bumped windowLoadGeneration while the fetch was in flight.
 * @param onRows Callback invoked when prefetch returns rows.
 * @param errorMessage Log message used on prefetch failure.
 * @returns Nothing.
 */
const prefetchAndHydrateRows = async (
  direction: GuideTimeWindow,
  getState: () => unknown,
  epoch: number,
  onRows: (payload: {
    rows: TGridGuideRow[];
    timeWindow: GuideTimeWindow;
    startGlobalIndex: number;
  }) => void,
  errorMessage: string
): Promise<void> => {
  try {
    const prefetchResult = await resolvePrefetchRows(
      (getState() as GuideThunkState).guide,
      direction
    );

    // Latest wins: a newer LEFT/RIGHT/settle has moved on, so these rows would
    // land in the wrong (rotated) bucket. Discard them.
    if ((getState() as GuideThunkState).guide.windowLoadGeneration !== epoch) {
      return;
    }

    if (prefetchResult !== null) {
      onRows(prefetchResult);
    }
  } catch (error: unknown) {
    Log.e(TAG, errorMessage, error);
  }
};

/**
 * Extends the cached time range by one prefetch chunk beyond the visible window
 * edge in the move direction, for all windowed channels. Best-effort: failures
 * are logged and swallowed so navigation is never blocked.
 *
 * @param getState Thunk getState function.
 * @param extendFuture True when moving right (forward); false when moving left.
 * @param epoch windowLoadGeneration captured after the shift; the prefetch is skipped when
 *   a newer navigation has bumped windowLoadGeneration.
 * @returns Nothing.
 */
const prefetchTimeForDirection = async (
  getState: () => unknown,
  extendFuture: boolean,
  epoch: number
): Promise<void> => {
  const state: GuideState = (getState() as GuideThunkState).guide;
  if (state.currentWindowStartTime === null) {
    return;
  }
  if (state.windowLoadGeneration !== epoch) {
    return;
  }
  const window: GuideWindowChannels = getGuideWindowChannels(state);
  if (window.channels.length === 0) {
    return;
  }
  const cache: GuideCache = GuideCache.getInstance();
  const remainingBufferSlots: number = cache.getRemainingTimeBufferSlots(
    state.currentWindowStartTime,
    extendFuture
  );
  if (!shouldPrefetchTime(remainingBufferSlots)) {
    return;
  }
  try {
    await cache.prefetchTime(
      state.currentWindowStartTime,
      window.channels,
      extendFuture
    );
  } catch (error: unknown) {
    Log.e(TAG, "navigateGuide — time prefetch failed", error);
  }
};

/**
 * Navigates guide focus right by one program cell.
 *
 * Current guide:
 *   - Sidebar focused: clears sidebar highlight, returns focus to channel cell (hPos 0).
 *   - Stall (last cell): shifts the time axis forward one GUIDE_SHIFT_MINUTES step
 *     and pre-fetches the next future window.
 *
 * Past guide (no filter sidebar; right-edge order: programs → channel cell → current guide):
 *   - Stall (last program cell) in the immediate past window (bordering the
 *     current guide): moves focus to the channel cell (hPos 0).
 *   - Stall (last program cell) in any window further back: shifts the time
 *     axis forward one step without exposing the channel cell.
 *   - Channel cell: shifts the time axis forward one step toward the current
 *     guide, pre-fetching the next future window.
 */
export const navigateGuideRight = createAsyncThunk<void, void>(
  "guide/navigateGuideRight",
  async (_arg, { dispatch, getState }) => {
    scheduleGuideSettle(dispatch);
    const currentState: GuideState = (getState() as GuideThunkState).guide;
    const isTurbo: boolean = currentState.navMode === GuideNavMode.TURBO;

    // An empty filtered grid leaves only the sidebar navigable.
    if (isSidebarPinnedByEmptyGrid(currentState)) {
      return;
    }

    // Sidebar is only reachable in the current guide: RIGHT exits it back to
    // the grid.
    if (currentState.isSidebarFocused) {
      dispatch(closeGuideFilterSidebar());
      return;
    }

    // Past guide: the channel cell (rendered on the right edge) is the last
    // stop, so RIGHT there shifts the time axis forward toward the current
    // guide. Every other position steps one cell and only shifts on a stall.
    const isPastChannelCell: boolean =
      currentState.isPastGuide && currentState.horizontalPosition === 0;

    if (!isPastChannelCell) {
      const preHPos: number = currentState.horizontalPosition;

      dispatch(_navigateGuideRightStep());

      const postHPos: number = (getState() as GuideThunkState).guide
        .horizontalPosition;

      if (!shouldShiftForwardAfterRightStep(preHPos, postHPos)) {
        return;
      }

      // Past guide stall: move focus to the channel cell (rendered right of
      // the program cells in past guide), but only when this window
      // immediately borders the current guide — deeper past windows keep
      // shifting forward instead of exposing the channel cell.
      const postStepState: GuideState = (getState() as GuideThunkState).guide;
      if (
        postStepState.isPastGuide &&
        isImmediatePastWindow(
          postStepState.currentWindowStartTime,
          postStepState.guideSessionStartMs
        )
      ) {
        dispatch(setGuideHighlightHorizontalPosition(0));
        return;
      }
    }

    // Guide-rows service failure: no program schedule exists for the future
    // window either, so RIGHT stalls at the single tune-fallback cell rather
    // than shifting the time axis forward.
    if (currentState.isGuideRowsFailed) {
      return;
    }

    // Stall: was at last cell — shift time axis forward. In turbo the fetch is
    // deferred to settle (on key release); a discrete press pre-fetches now.
    dispatch(shiftTimeAxisForward());

    if (!isTurbo) {
      const epoch: number = (getState() as GuideThunkState).guide
        .windowLoadGeneration;
      await prefetchAndHydrateRows(
        GuideTimeWindow.FUTURE,
        getState,
        epoch,
        (payload) => {
          dispatch(mergeGuideRows(payload));
        },
        "navigateGuideRight — pre-fetch failed"
      );

      await prefetchTimeForDirection(getState, true, epoch);
    }
  }
);

/**
 * Navigates guide focus left by one program cell.
 *
 * Non-present window guard: if moving from the first program cell (hPos 1) to
 * the channel cell (hPos 0) while in a non-session window, shifts the time
 * axis backward instead of allowing channel-cell focus.
 *
 * Channel-cell stall (current guide only):
 *   - First LEFT at hPos 0: highlights the filter sidebar icon (left edge).
 *   - Second LEFT while sidebar is highlighted: shifts the time axis backward
 *     into the past guide and pre-fetches the next past window.
 *
 * Channel-cell stall (past guide): the channel cell sits on the right edge, so
 * LEFT returns focus to the last program cell (no time shift).
 */
export const navigateGuideLeft = createAsyncThunk<void, void>(
  "guide/navigateGuideLeft",
  async (_arg, { dispatch, getState }) => {
    scheduleGuideSettle(dispatch);
    const preState: GuideState = (getState() as GuideThunkState).guide;
    const isTurbo: boolean = preState.navMode === GuideNavMode.TURBO;

    // Early exit: directional lock blocks LEFT while sidebar is focused.
    if (preState.isSidebarFocused) {
      const elapsed: number = Date.now() - preState.sidebarEnteredAtMs;
      if (elapsed < GUIDE_SIDEBAR_DIRECTIONAL_LOCK_MS) {
        return;
      }
    }

    const preHPos: number = preState.horizontalPosition;
    const preWindowStartTime: DateTime | null = preState.currentWindowStartTime;
    const sessionStartMs: number = preState.guideSessionStartMs;

    dispatch(_navigateGuideLeftStep());

    const postHPos: number = (getState() as GuideThunkState).guide
      .horizontalPosition;

    // Non-present window guard: moved from first program cell to channel cell
    // while in a non-session window — shift backward instead.
    if (
      shouldTriggerNonPresentWindowGuardOnLeft(
        preHPos,
        postHPos,
        preWindowStartTime,
        sessionStartMs
      )
    ) {
      if (preWindowStartTime === null) {
        return;
      }

      // Guide-rows service failure: no program schedule exists for the past
      // window either, so LEFT stalls rather than shifting the time axis
      // backward.
      if (preState.isGuideRowsFailed) {
        return;
      }

      const preShiftWindowMs: number = preWindowStartTime.getTime();
      dispatch(
        shiftTimeAxisBackward(
          (getState() as UnityRootState).branding.epgLookBackHours
        )
      );

      const postShiftState: GuideState = (getState() as GuideThunkState).guide;
      const postShiftWindowTime: DateTime | null =
        postShiftState.currentWindowStartTime;
      const shiftBlockedAtBoundary: boolean = isBackwardShiftBlockedAtBoundary(
        preShiftWindowMs,
        postShiftWindowTime
      );

      if (shiftBlockedAtBoundary && postShiftState.horizontalPosition === 0) {
        dispatch(setGuideHighlightHorizontalPosition(1));
      }

      if (!isTurbo) {
        const preShiftEpoch: number = postShiftState.windowLoadGeneration;
        await prefetchAndHydrateRows(
          GuideTimeWindow.PAST,
          getState,
          preShiftEpoch,
          (payload) => {
            dispatch(mergeGuideRows(payload));
          },
          NAVIGATE_LEFT_PREFETCH_ERROR
        );
        await prefetchTimeForDirection(getState, false, preShiftEpoch);
      }
      return;
    }

    // Channel-cell stall: already at hPos 0.
    if (preHPos === postHPos && preHPos === 0) {
      await handleLeftChannelCellStall(dispatch, getState);
    }
  }
);

// ─── Jump-to-Channel Thunks ──────────────────────────────────────────────────

/**
 * Clears all module-level jump-to-channel timers.
 * Exported for component unmount cleanup.
 */
export const clearGuideJumpTimers = (): void => {
  if (jumpEntryTimeoutId !== null) {
    clearTimeout(jumpEntryTimeoutId);
    jumpEntryTimeoutId = null;
  }
  if (jumpErrorTimeoutId !== null) {
    clearTimeout(jumpErrorTimeoutId);
    jumpErrorTimeoutId = null;
  }
};

/**
 * Commits the accumulated jump-to-channel digits: resolves the channel,
 * applies the scroll/focus jump on success, or shows the "Not found" error
 * state on failure.
 *
 * This is an internal thunk used by `handleGuideDigitKey` (on timer expiry)
 * and `flushGuideJumpEntry` (on non-digit key press).
 */
const commitJumpToChannel =
  () =>
  async (
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState
  ): Promise<void> => {
    // Clear the entry timer (may already be null if called from timer callback).
    if (jumpEntryTimeoutId !== null) {
      clearTimeout(jumpEntryTimeoutId);
      jumpEntryTimeoutId = null;
    }

    const state: GuideState = (getState() as GuideThunkState).guide;
    const digits: string = state.jumpToChannel.digits;

    if (digits.length === 0) {
      dispatch(_jumpReset());
      return;
    }

    const effectiveIndex: number | null = resolveJumpChannelIndex(
      state,
      digits
    );

    if (effectiveIndex === null) {
      Log.w(TAG, "commitJumpToChannel() — no match for: " + digits);
      DiagnosticLogger.log(
        "guide_nav",
        "jump-to-channel no_match",
        DiagnosticSeverity.Info,
        { digits, outcome: "no_match" }
      );
      dispatch(_jumpSetError());

      // Auto-dismiss the error after GUIDE_JUMP_ERROR_DISPLAY_MS.
      if (jumpErrorTimeoutId !== null) {
        clearTimeout(jumpErrorTimeoutId);
      }
      jumpErrorTimeoutId = setTimeout((): void => {
        jumpErrorTimeoutId = null;
        dispatch(_jumpReset());
      }, GUIDE_JUMP_ERROR_DISPLAY_MS);
      return;
    }

    const effectiveCount: number = getEffectiveChannelCount(state);
    const target: JumpScrollTarget = computeJumpScrollTarget(
      effectiveIndex,
      effectiveCount
    );

    Log.i(
      TAG,
      "commitJumpToChannel() — entered: " +
        digits +
        ", resolved effectiveIndex: " +
        effectiveIndex
    );
    DiagnosticLogger.log(
      "guide_nav",
      "jump-to-channel resolved",
      DiagnosticSeverity.Info,
      { digits, outcome: "resolved" }
    );

    dispatch(_applyJumpToChannel(target));

    // Trigger program-data fetch for the newly visible region (mirrors
    // the post-scroll pattern in navigateGuideUp/Down).
    dispatch(_incrementWindowLoadGeneration());
    await dispatch(loadGuideWindow());
  };

/**
 * Handles a single digit key press (0–9) during guide grid focus.
 *
 * On each press:
 * 1. Ignores the digit when the sidebar is focused, the guide is loading, or
 *    the guide error screen is shown.
 * 2. If the error state is currently displayed, resets it before accepting digits.
 * 3. Silently discards the digit (without restarting the timer) when the buffer
 *    has reached `GUIDE_JUMP_MAX_DIGITS`.
 * 4. Appends the digit, shows the overlay, and restarts the 1500 ms timer.
 *    When the timer fires, `commitJumpToChannel()` is dispatched.
 */
export const handleGuideDigitKey =
  (digit: string) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const state: GuideState = (getState() as GuideThunkState).guide;

    // Ignore digits when the grid is not interactable.
    if (state.isSidebarFocused || state.isLoading || state.error.length > 0) {
      return;
    }

    // If the error overlay is showing, clear it before accepting new input.
    if (state.jumpToChannel.isError) {
      if (jumpErrorTimeoutId !== null) {
        clearTimeout(jumpErrorTimeoutId);
        jumpErrorTimeoutId = null;
      }
      dispatch(_jumpReset());
    }

    // Re-read state after potential reset.
    const currentDigits: string = (getState() as GuideThunkState).guide
      .jumpToChannel.digits;

    // Max-digit overflow: silently ignore WITHOUT restarting the timer.
    if (currentDigits.length >= GUIDE_JUMP_MAX_DIGITS) {
      return;
    }

    dispatch(_jumpAppendDigit(digit));

    // Restart the per-digit inactivity timer.
    if (jumpEntryTimeoutId !== null) {
      clearTimeout(jumpEntryTimeoutId);
    }
    jumpEntryTimeoutId = setTimeout((): void => {
      jumpEntryTimeoutId = null;
      dispatch(commitJumpToChannel());
    }, GUIDE_JUMP_ENTRY_TIMEOUT_MS);
  };

/**
 * If digits have been accumulated, commits the jump immediately.
 * Called by non-digit key handlers (§6 of the spec) before their own action
 * so that the accumulated entry is not lost.
 */
export const flushGuideJumpEntry =
  () =>
  async (
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState
  ): Promise<void> => {
    const state: GuideState = (getState() as GuideThunkState).guide;
    if (
      state.jumpToChannel.digits.length > 0 ||
      state.jumpToChannel.isVisible
    ) {
      await dispatch(commitJumpToChannel());
    }
  };

/**
 * Discards an in-progress jump-to-channel entry outright — clears the
 * pending timers and resets the overlay/digit state without committing a
 * jump. Unlike `flushGuideJumpEntry` (which commits accumulated digits),
 * this is for callers that need the overlay gone with no side effect, e.g.
 * the app backgrounding mid-entry.
 */
export const resetGuideJumpEntry =
  () =>
  (dispatch: UnityAppDispatch): void => {
    clearGuideJumpTimers();
    dispatch(_jumpReset());
  };

// ─── Selectors ───

/** Selects whether guide data is currently loading. */
export const selectIsGuideLoading = (state: UnityRootState): boolean =>
  state.guide.isLoading;

/** Selects the current guide error message. */
export const selectGuideError = (state: UnityRootState): string =>
  state.guide.error;

/**
 * Selects whether the guide-rows (EPG program schedule) service has failed
 * to load data for the current window.
 */
export const selectIsGuideRowsFailed = (state: UnityRootState): boolean =>
  state.guide.isGuideRowsFailed;

/** Selects the channel items for the guide grid. */
export const selectGuideChannels = (state: UnityRootState): TChannel[] =>
  state.guide.channels;

/** Selects the current guide window start time. */
export const selectCurrentWindowStartTime = (
  state: UnityRootState
): DateTime | null => state.guide.currentWindowStartTime;

/** Selects the guide start time used for rendering the guide grid. */
export const selectGuideStartTime = (state: UnityRootState): DateTime | null =>
  getGuideStartTime(state.guide);

/** Selects the time bar date label for the guide header timebar. */
export const selectGuideTimeBarLabel = (state: UnityRootState): string => {
  const startTime: DateTime | null = getGuideStartTime(state.guide);
  if (startTime === null) {
    return "";
  }
  return startTime.isToday()
    ? Locale.getString(LocaleKeys.GUIDE_TIMEBAR_TODAY)
    : startTime.toLocaleDayWithDate(DeviceModel.getTVProperties().getLang());
};

/** Selects whether the guide is displaying past program data. */
export const selectIsPastGuide = (state: UnityRootState): boolean =>
  state.guide.isPastGuide;

/** Selects whether the filter sidebar icon is currently highlighted. */
export const selectHasFavoriteChannels = (state: UnityRootState): boolean =>
  state.guide.hasFavoriteChannels;

export const selectIsGuideSidebarFocused = (state: UnityRootState): boolean =>
  state.guide.isSidebarFocused;

/** Selects the index of the currently highlighted filter sidebar category. */
export const selectGuideSidebarFocusIndex = (state: UnityRootState): number =>
  state.guide.sidebarFocusIndex;

/** Selects the currently focused vertical row position. */
export const selectGuideHighlightVerticalPosition = (
  state: UnityRootState
): number => state.guide.verticalPosition;

/** Selects the currently focused horizontal position in the row. */
export const selectGuideHighlightHorizontalPosition = (
  state: UnityRootState
): number => state.guide.horizontalPosition;

/**
 * Selects the effective (clamped) vertical row position.
 * Clamps verticalPosition to the lock-zone maximum (GUIDE_HIGHLIGHT_ROW_MAX).
 */
export const selectEffectiveGuideHighlightVerticalPosition = (
  state: UnityRootState
): number => Math.min(state.guide.verticalPosition, GUIDE_HIGHLIGHT_ROW_MAX);

/**
 * Selects the effective (clamped) horizontal position for the currently focused row.
 * Uses channelScrollOffset to resolve the absolute channel index before looking up
 * the row's guide cells.
 * Horizontal position: 0 = channel cell, 1..N = program cell index + 1.
 */
export const selectEffectiveGuideHighlightHorizontalPosition = (
  state: UnityRootState
): number => {
  const effectiveVerticalPosition: number =
    selectEffectiveGuideHighlightVerticalPosition(state);
  const horizontalPosition: number = state.guide.horizontalPosition;

  if (horizontalPosition < 0) {
    return 0;
  }

  // Uses the rendered-cell count (real programs + loading placeholders) so the
  // clamp matches what the user can actually focus, including loading cells.
  const maxHorizontalPosition: number = getMaxHorizontalPosition(
    state.guide,
    effectiveVerticalPosition
  );

  return Math.min(horizontalPosition, maxHorizontalPosition);
};

/** Selects the channel scroll offset (index of the first visible channel). */
export const selectChannelScrollOffset = (state: UnityRootState): number =>
  state.guide.channelScrollOffset;

/** Selects the guide session start time in milliseconds (set on first mount). */
export const selectGuideSessionStartMs = (state: UnityRootState): number =>
  state.guide.guideSessionStartMs;

/** Selects the anchor time of the pre-fetched future window. */
export const selectFutureWindowStartTime = (
  state: UnityRootState
): DateTime | null => state.guide.futureWindowStartTime;

/** Selects the anchor time of the pre-fetched past window. */
export const selectPastWindowStartTime = (
  state: UnityRootState
): DateTime | null => state.guide.pastWindowStartTime;

/** Selects the rendered time-slot labels for the guide header. */
export const selectGuideTimeSlots = (
  state: UnityRootState
): Array<{ label: string }> => buildTimeSlotLabels(state.guide);

/** Selects the render-ready visible guide rows for the screen. */
export const selectGuideVisibleRows = createSelector(
  (state: UnityRootState) => state.guide,
  (guide: GuideState): GuideVisibleRowView[] => buildVisibleGuideRows(guide)
);

/** Selects the current navigation epoch (bumps on every load cycle). */
export const selectGuideWindowLoadGeneration = (
  state: UnityRootState
): number => state.guide.windowLoadGeneration;

/** Selects whether the visible window's loading cells have timed out. */
export const selectGuideLoadingTimedOut = (state: UnityRootState): boolean =>
  state.guide.guideLoadingTimedOut;

/**
 * Selects whether the visible grid currently shows any loading placeholder.
 * False in the guide-rows failure (ERROR) state — that is not "loading".
 */
export const selectGuideHasLoadingCells = createSelector(
  selectGuideVisibleRows,
  (state: UnityRootState): boolean => state.guide.isGuideRowsFailed,
  (visibleRows: GuideVisibleRowView[], isGuideRowsFailed: boolean): boolean => {
    if (isGuideRowsFailed) {
      return false;
    }
    for (let rowIndex: number = 0; rowIndex < visibleRows.length; rowIndex++) {
      const cells = visibleRows[rowIndex].primaryPrograms;
      for (let cellIndex: number = 0; cellIndex < cells.length; cellIndex++) {
        if (cells[cellIndex].uiProps.isPlaceholder === true) {
          return true;
        }
      }
    }
    return false;
  }
);

/**
 * Selects the GUIDE_VISIBLE_ROW_COUNT visible channels derived from channelScrollOffset.
 * Returns objects containing the channel and its absolute index in the channels array.
 * The absolute index is used to look up the matching guide rows.
 */
export const selectVisibleChannels = (
  state: UnityRootState
): GuideVisibleChannelRow[] => {
  return buildVisibleChannelRows(state.guide);
};

/**
 * Selects the focused channel derived from the effective vertical position.
 * Delegates to selectVisibleChannels so that channelScrollOffset is accounted for.
 */
export const selectFocusedGuideChannel = (
  state: UnityRootState
): TChannel | null => {
  const visibleChannels: GuideVisibleChannelRow[] =
    selectVisibleChannels(state);
  const effectiveVerticalPosition: number =
    selectEffectiveGuideHighlightVerticalPosition(state);
  const focusedRow: GuideVisibleChannelRow | undefined =
    visibleChannels[effectiveVerticalPosition];

  if (focusedRow === null || focusedRow === undefined) {
    return null;
  }

  return focusedRow.channel;
};

// Private: resolves the focused GuideCellView so both public selectors
// (cell data + placeholder flag) share a single lookup.
const selectFocusedGuideCellView = createSelector(
  selectGuideVisibleRows,
  selectEffectiveGuideHighlightVerticalPosition,
  selectEffectiveGuideHighlightHorizontalPosition,
  (
    visibleRows: GuideVisibleRowView[],
    effectiveVerticalPosition: number,
    effectiveHorizontalPosition: number
  ): GuideCellView | null => {
    if (effectiveHorizontalPosition === 0) {
      return null;
    }

    const focusedVisibleRow: GuideVisibleRowView | undefined =
      visibleRows[effectiveVerticalPosition];

    if (focusedVisibleRow === null || focusedVisibleRow === undefined) {
      return null;
    }

    const primaryRow = focusedVisibleRow.primaryPrograms;

    if (
      primaryRow === null ||
      primaryRow === undefined ||
      primaryRow.length === 0 ||
      primaryRow.length < effectiveHorizontalPosition ||
      primaryRow[effectiveHorizontalPosition - 1] === null ||
      primaryRow[effectiveHorizontalPosition - 1] === undefined
    ) {
      return null;
    }

    return primaryRow[effectiveHorizontalPosition - 1];
  }
);

/**
 * Selects the focused guide program cell.
 * Returns null when focus is on channel cell (horizontal position = 0) or
 * out of bounds. Returns the cell even when it is a loading placeholder —
 * callers that must not act on a placeholder use
 * {@link selectIsFocusedGuideCellPlaceholder}.
 * Memoized via createSelector: only recomputes when visible rows or focus
 * positions change, ensuring a stable reference is returned across
 * notification bursts (prevents infinite re-render during fast navigation).
 */
export const selectFocusedGuideCell = createSelector(
  selectFocusedGuideCellView,
  (cellView: GuideCellView | null): TGuideCell | null => {
    if (cellView === null) {
      return null;
    }
    return cellView.props;
  }
);

/** True when the focused program cell is a loading placeholder. */
export const selectIsFocusedGuideCellPlaceholder = createSelector(
  selectFocusedGuideCellView,
  (cellView: GuideCellView | null): boolean => {
    if (cellView === null) {
      return false;
    }
    return cellView.uiProps.isPlaceholder === true;
  }
);

/** Selects the IDs of the currently selected guide filters (multi-select). */
export const selectSelectedGuideFilterIds = (state: UnityRootState): string[] =>
  state.guide.selectedFilterIds;

/** Selects whether the Favorites filter is currently active (channel list narrowed to favorites). */
export const selectIsFavoritesFilterActive = (state: UnityRootState): boolean =>
  isFavoritesFilterSelected(state.guide);

/** Selects the dynamically loaded guide filter items for the sidebar. */
export const selectGuideFilterItems = (
  state: UnityRootState
): TGuideFilterItem[] => state.guide.filterItems;

/** Selects the last resolved `/v2/guideChannelIds` result for the current non-favorite selection. */
export const selectCategoryFilterChannelIds = (
  state: UnityRootState
): string[] => state.guide.categoryFilterChannelIds;

/** Selects the tri-state resolution status of the non-favorite (category) filter selection. */
export const selectCategoryFilterResolutionStatus = (
  state: UnityRootState
): "idle" | "resolved" | "failed" => state.guide.categoryFilterResolutionStatus;

/** Selects whether the filter sidebar's expanded (multi-select overlay) presentation is open. */
export const selectIsGuideFilterSidebarExpanded = (
  state: UnityRootState
): boolean => state.guide.isFilterSidebarExpanded;

/**
 * True exactly when the grid should show the "no channels match"/"couldn't
 * apply filter" empty state: a non-favorite (category) filter is selected,
 * Favorites is NOT also active (Favorites always "rescues" the view — a
 * category failure or empty result never hides an active Favorites view),
 * and the last resolution attempt either failed or came back with zero
 * matching channels.
 */
export const selectShouldShowGuideFilterEmptyState = (
  state: UnityRootState
): boolean => isGuideGridEmptyByFilter(state.guide);

/** True when a filter leaves the grid empty, so only the filter sidebar is navigable. */
export const selectIsGuideGridEmptyByFilter = (
  state: UnityRootState
): boolean => isGuideGridEmptyByFilter(state.guide);

/** Selects whether NPVR recording is allowed at the device/account level. */
export const selectIsNpvrRecordingAllowed = (state: UnityRootState): boolean =>
  state.guide.isNpvrRecordingAllowed;

/** Selects the guide offer preview data for the focused program cell. */
export const selectGuidePreviewData = (
  state: UnityRootState
): TPreview | null => state.guide.previewData;

/** Selects the offerId `selectGuidePreviewData` was fetched for. */
export const selectGuidePreviewDataOfferId = (
  state: UnityRootState
): string | null => state.guide.previewDataOfferId;

/** Selects whether a guide offer preview fetch is currently in progress. */
export const selectIsGuidePreviewLoading = (state: UnityRootState): boolean =>
  state.guide.isPreviewLoading;

/**
 * Memoized selector: true when NPVR recording is allowed AND the focused
 * channel's policy permits recording. Encapsulates ChannelsInteractor logic
 * so views never call domain interactors directly.
 */
export const selectIsFocusedGuideChannelRecordable = createSelector(
  selectFocusedGuideChannel,
  selectIsNpvrRecordingAllowed,
  (channel: TChannel | null, isNpvrRecordingAllowed: boolean): boolean => {
    if (channel === null) {
      return false;
    }
    return (
      isNpvrRecordingAllowed && ChannelsInteractor.isChannelRecordable(channel)
    );
  }
);

/**
 * Memoized selector: true when the focused channel has a SOCU policy with a
 * non-zero catch-up availability window (cuAvailabilityDurationSeconds > 0).
 * Used by ChannelPreview to render the SOCU decoration badge.
 */
export const selectFocusedGuideChannelHasSocu = createSelector(
  selectFocusedGuideChannel,
  (channel: TChannel | null): boolean => {
    if (channel === null) {
      return false;
    }
    // TiVo+ channels offer no start-over or catch-up, so the badge must not
    // appear even if a policy window arrives in the lineup data.
    if (ChannelsInteractor.isTivoPlusChannel(channel)) {
      return false;
    }
    const policyInfo = channel.policyInfo;
    if (policyInfo === null || policyInfo === undefined) {
      return false;
    }
    for (let i: number = 0; i < policyInfo.length; i++) {
      const socuRules = policyInfo[i].socuRules;
      if (
        socuRules !== null &&
        socuRules !== undefined &&
        (socuRules.cuAvailabilityDurationSeconds || 0) > 0
      ) {
        return true;
      }
    }
    return false;
  }
);

/**
 * Memoized selector: true when the focused channel is a jump channel
 * (i.e. has jumpChannelInfo defined). Used by ChannelPreview to render
 * a simplified preview layout for jump channels.
 */
export const selectIsFocusedGuideChannelJump = createSelector(
  selectFocusedGuideChannel,
  (channel: TChannel | null): boolean => {
    if (channel === null) {
      return false;
    }
    return channel.jumpChannelInfo !== undefined;
  }
);

/**
 * Memoized selector: true when the focused channel qualifies as a TiVo Plus
 * channel. Encapsulates ChannelsInteractor.isTivoPlusChannel so views never
 * call domain interactors directly.
 */
export const selectFocusedGuideChannelIsTivoPlus = createSelector(
  selectFocusedGuideChannel,
  (channel: TChannel | null): boolean => {
    if (channel === null) {
      return false;
    }
    return ChannelsInteractor.isTivoPlusChannel(channel);
  }
);

/**
 * Memoized selector: returns the app-based partner icon URL for the focused
 * channel when it is an AppLinear channel, or null otherwise.
 * Used by PreviewDecoration to render the partner source logo (e.g. Pluto).
 */
export const selectFocusedGuideChannelAppIconUrl = createSelector(
  selectFocusedGuideChannel,
  (channel: TChannel | null): string | null => {
    if (channel === null) {
      return null;
    }
    return ChannelsInteractor.getAppBasedIconUrl(channel, 39, 35);
  }
);

/**
 * The channel's catch-up availability window in seconds, from the first
 * policy entry that declares SOCU rules.
 *
 * Extracted from {@link selectIsFocusedProgramSocuAvailable} to keep that
 * selector under the cognitive-complexity limit.
 *
 * @returns The window length, or `0` when the channel declares none.
 */
const getCatchUpWindowSeconds = (channel: TChannel | null): number => {
  if (channel === null) {
    return 0;
  }

  const policyInfo = channel.policyInfo;

  if (policyInfo === null || policyInfo === undefined) {
    return 0;
  }

  for (let i: number = 0; i < policyInfo.length; i++) {
    const socuRules = policyInfo[i].socuRules;
    if (socuRules !== null && socuRules !== undefined) {
      return socuRules.cuAvailabilityDurationSeconds || 0;
    }
  }

  return 0;
};

/**
 * Memoized selector: true when the focused guide program is currently
 * available via start-over or catch-up.
 *
 * - Start-over: program is live now (`isStartover` flag set and current time
 *   is within the program window).
 * - Catch-up: program has ended (`isCatchup` flag set), and the current time
 *   is within the channel's catch-up availability window.
 *
 * `Date.now()` is evaluated at select/render time (consistent with the
 * live-clock pattern used in `handleKeyOk` and `isFutureProgram`).
 */
export const selectIsFocusedProgramSocuAvailable = createSelector(
  selectFocusedGuideCell,
  selectIsFocusedGuideCellPlaceholder,
  selectFocusedGuideChannel,
  (
    cell: TGuideCell | null,
    isPlaceholder: boolean,
    channel: TChannel | null
  ): boolean => {
    if (cell === null || isPlaceholder) {
      return false;
    }

    // TiVo+ programmes are live-only: neither start-over nor catch-up applies,
    // so the launch path must never treat one as available.
    if (channel !== null && ChannelsInteractor.isTivoPlusChannel(channel)) {
      return false;
    }

    const startTimeMs: number =
      cell.startTime !== null && cell.startTime !== undefined
        ? cell.startTime.getTime()
        : 0;
    const durationSeconds: number =
      cell.duration !== null && cell.duration !== undefined ? cell.duration : 0;
    const endTimeMs: number = startTimeMs + durationSeconds * 1000;
    const nowMs: number = Date.now();

    const isStartOver: boolean =
      cell.isStartover === true && nowMs >= startTimeMs && nowMs < endTimeMs;

    const cuAvailabilityDurationSeconds: number =
      getCatchUpWindowSeconds(channel);

    const isCatchup: boolean =
      cell.isCatchup === true &&
      endTimeMs < nowMs &&
      (cuAvailabilityDurationSeconds <= 0 ||
        nowMs <= startTimeMs + cuAvailabilityDurationSeconds * 1000);

    return isStartOver || isCatchup;
  }
);

/**
 * Memoized selector: returns the logo URL for the focused guide channel.
 * Handles both regular channels (logoIndex) and jump channels (applicationId).
 * Returns an empty string when no channel is focused.
 * Encapsulates ChannelsInteractor.getChannelUrl so views never call
 * domain interactors directly.
 */
export const selectFocusedGuideChannelLogoUrl = createSelector(
  selectFocusedGuideChannel,
  (channel: TChannel | null): string => {
    if (channel === null) {
      return "";
    }
    const isJumpChannel: boolean = channel.jumpChannelInfo !== undefined;
    return (
      ChannelsInteractor.getChannelUrl({
        height: 225,
        width: 300,
        ...(isJumpChannel
          ? {
              isJumpChannel: true,
              objectId:
                ((channel.jumpChannelInfo || {}).appLaunchInfo || {})
                  .applicationId || ""
            }
          : {
              isJumpChannel: false,
              logoIndex: channel.logoIndex || 0
            })
      }) || ""
    );
  }
);

// ─── Jump-to-Channel Selectors ────────────────────────────────────────────────

/** Selects the accumulated digits for the jump-to-channel overlay. */
export const selectGuideJumpDigits = (state: UnityRootState): string =>
  state.guide.jumpToChannel.digits;

/** Selects whether the jump-to-channel overlay is visible. */
export const selectIsGuideJumpVisible = (state: UnityRootState): boolean =>
  state.guide.jumpToChannel.isVisible;

/** Selects whether the jump-to-channel overlay is in the error state. */
export const selectIsGuideJumpError = (state: UnityRootState): boolean =>
  state.guide.jumpToChannel.isError;

export default guideSlice.reducer;
